<template>
  <div
    class="smart-orb-root"
    :class="orbRootClass"
    :style="orbRootStyle"
  >
    <transition name="knowledge-portal">
      <div v-if="knowledgePortalActive" class="knowledge-portal-overlay">
        <div class="knowledge-portal-ring"></div>
        <div class="knowledge-portal-core"></div>
      </div>
    </transition>

    <div
      v-if="isKnowledgeOrbFlying"
      :class="['knowledge-orb-flight', knowledgeOrbFlightVariantClass]"
      :style="knowledgeOrbFlightStyle"
    ></div>

    <transition name="panel-slide">
      <div v-if="panelOpen" class="dialog-panel" :style="dialogPanelStyle">
        <div class="panel-header">
          <span>古贤智能助手</span>
          <button class="panel-close" @click="closeChatPanel">×</button>
        </div>
        <div v-if="multimodalState.target || multimodalState.voice.createdTask" class="multimodal-context-strip">
          <div v-if="multimodalState.target"><span>目标</span><strong>{{ multimodalState.target.name }}</strong></div>
          <div v-if="multimodalState.target"><span>手势</span><strong>FIST · 已锁定</strong></div>
          <div v-if="multimodalState.voice.utterance"><span>语音</span><strong>{{ multimodalState.voice.utterance }}</strong></div>
          <div v-if="multimodalState.voice.understanding"><span>理解</span><strong>{{ multimodalState.voice.understanding }}</strong></div>
          <div v-if="multimodalState.voice.createdTask"><span>任务</span><strong class="task-created-tag">{{ multimodalState.voice.createdTask.title }} ({{ multimodalState.voice.createdTask.rootTaskId }})</strong></div>
        </div>
        <div class="panel-body" ref="chatBody">
          <div v-for="(msg, i) in chatHistory" :key="i"
               :class="['chat-msg', msg.role === 'user' ? 'msg-user' : 'msg-ai']">
            <div class="msg-bubble">{{ msg.text }}</div>
          </div>
          <div v-if="isThinking" class="typing-indicator">
            <span></span><span></span><span></span>
          </div>
        </div>
        <div class="quick-actions">
          <button
            v-for="action in quickActions"
            :key="action.label"
            @click="runQuickAction(action)"
          >
            {{ action.label }}
          </button>
        </div>
        <div class="panel-input">
          <input v-model="textInput" @keydown.enter="handleTextSubmit"
                 placeholder="输入指令或问题..." class="text-input" />
          <button class="send-btn" @click="handleTextSubmit">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/></svg>
          </button>
          <button class="mic-btn" :class="{ active: isListening, pending: isListeningStarting }" @click="toggleListening">
            <svg v-if="!isListening" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1"/></svg>
          </button>
        </div>
      </div>
    </transition>

    <transition name="idle-bubble">
      <button v-if="showIdleBubble" class="idle-prompt-bubble" :style="idleBubbleStyle" @click.stop="useIdlePrompt">
        {{ idlePromptText }}
      </button>
    </transition>

    <div
      class="orb-wrapper"
      @click="togglePanel"
      @mousedown="startDrag"
      @touchstart.passive="startDrag"
    >
      <div class="siri-orb" :class="orbStateClass" :style="orbSizeStyle">
        <div class="orb-glow" :class="orbStateClass"></div>
      </div>
      <p v-if="orbState === 'offline'" class="hint-text">设备未连接</p>
      <p v-else-if="isListeningStarting" class="hint-text">正在连接麦克风…</p>
      <p v-else-if="isListening" class="hint-text listening-hint">正在聆听...</p>
    </div>
  </div>
</template>

<script>
import { conversationStateMachine as ConversationStateMachine } from './siri-interface/core/ConversationStateMachine.js'
import { panelStore } from '@/panelStore.js'
import { ACTION_PANEL_MAP } from '@/services/llmService.js'
import { resolveBusinessDataQuestion } from '@/services/businessDataService.js'
import { compareDispatch, findReservoirByName, runFloodScenario, runHuadongModelChain } from '@/services/hydroService.js'
import { getFloodFrequencyConfig, scaleFloodSchemeForFrequency } from '@/services/floodFrequency.js'
import { routePanelIntent } from '@/utils/voiceIntentRouter.js'
import { normalizeForTTS } from '@/utils/ttsNormalize.js'
import { getActiveScenarioInfo, switchScenario, listScenarios, getActiveScenarioKey } from '@/services/emergencyMapData.js'
import * as Cesium from 'cesium'
import { isCompetitionRuntimeEnabled } from '@/services/competitionRuntime.js'
import { multimodalContext } from '@/services/multimodalContext.js'
import { createForecastTaskForReservoir } from '@/components/competition/taskAdapterRegistry.js'

const STATE_COLORS = {
  offline:   { c1: 'oklch(40% 0.01 264)', c2: 'oklch(35% 0.01 200)', c3: 'oklch(38% 0.01 280)' },
  idle:      { c1: 'oklch(75% 0.15 350)', c2: 'oklch(80% 0.12 200)', c3: 'oklch(78% 0.14 280)' },
  listening: { c1: 'oklch(75% 0.2 160)', c2: 'oklch(80% 0.18 180)', c3: 'oklch(78% 0.2 170)' },
  thinking:  { c1: 'oklch(65% 0.2 300)', c2: 'oklch(70% 0.18 320)', c3: 'oklch(68% 0.2 290)' },
  answering: { c1: 'oklch(70% 0.18 150)', c2: 'oklch(75% 0.16 160)', c3: 'oklch(72% 0.2 155)' },
}

const IDLE_PROMPTS = [
  '要不要汇报当前工程运行态势？',
  '我可以做一场洪水演进模拟',
  '需要对比保守、均衡、泄洪优先调度吗？',
  '可以问我：薛峰水位现在多少？',
  '要不要查看供需缺口和预警？',
]

const DEFAULT_WORKFLOW_SLOTS = {
  flood: {
    frequency: '20年一遇',
    dispatchMode: '均衡调度',
    target: '下游城镇和施工区',
    peakFlow: 5200,
  },
  dispatch: {
    level: 617.2,
    peakFlow: 4200,
    objective: '防洪优先，兼顾供水和生态基流',
    schemes: ['保守调度', '均衡调度', '泄洪优先'],
  },
  supply: {
    area: '古贤供水区',
    period: '未来24小时',
  },
}

const FLOOD_SCHEME_EXPERTISE = {
  1: {
    dispatchMode: '保守调度',
    name: '下泄方案1',
    focus: '保守控泄',
    risk: '低风险',
    peakFlow: 4800,
    peakRelease: 1650,
    maxLevel: 617.92,
    peakReduction: 41,
    inundationArea: 1.8,
    peakTime: '第10小时',
    timeline: {
      time: ['0h', '2h', '4h', '6h', '8h', '10h', '12h', '16h', '20h', '24h'],
      inflow: [1120, 1680, 2460, 3380, 4210, 4680, 3920, 2850, 1960, 1320],
      release: [820, 980, 1160, 1320, 1480, 1650, 1580, 1390, 1160, 940],
      level: [616.20, 616.45, 616.88, 617.32, 617.70, 617.92, 617.82, 617.48, 617.05, 616.66],
      area: [0.2, 0.4, 0.7, 1.0, 1.5, 1.8, 1.6, 1.1, 0.6, 0.3],
    },
    controlTarget: '优先保护居民区，严格限制下游水位抬升',
    condition: '居民区敏感、下游转移窗口不足',
    expertComment: '这个方案通过分阶段小幅开闸削减龙门断面涨率，下游城镇和施工区压力最小，但代价是库水位抬升更明显，需要持续盯防库区水位和闸门安全余度。',
  },
  2: {
    dispatchMode: '均衡调度',
    name: '下泄方案2',
    focus: '均衡调度',
    risk: '中风险',
    peakFlow: 5200,
    peakRelease: 2050,
    maxLevel: 617.38,
    peakReduction: 34,
    inundationArea: 2.4,
    peakTime: '第9小时',
    timeline: {
      time: ['0h', '2h', '4h', '6h', '8h', '10h', '12h', '16h', '20h', '24h'],
      inflow: [1120, 1680, 2460, 3380, 4210, 4680, 3920, 2850, 1960, 1320],
      release: [980, 1280, 1560, 1840, 2050, 1980, 1840, 1580, 1260, 1040],
      level: [616.18, 616.34, 616.62, 616.96, 617.28, 617.38, 617.22, 616.90, 616.58, 616.32],
      area: [0.3, 0.6, 1.0, 1.5, 2.1, 2.4, 2.0, 1.3, 0.8, 0.4],
    },
    controlTarget: '兼顾居民区、农田和水库防洪库容',
    condition: '常规20年一遇洪水、下游预警已到位',
    expertComment: '这是推荐演示方案。它提前预泄，并在洪峰期稳定控泄，库水位和下游淹没范围都处于可控区间，适合作为当前会商的基准方案。',
  },
  3: {
    dispatchMode: '泄洪优先',
    name: '下泄方案3',
    focus: '快速泄洪',
    risk: '较高风险',
    peakFlow: 5600,
    peakRelease: 2480,
    maxLevel: 616.82,
    peakReduction: 24,
    inundationArea: 3.6,
    peakTime: '第8小时',
    timeline: {
      time: ['0h', '2h', '4h', '6h', '8h', '10h', '12h', '16h', '20h', '24h'],
      inflow: [1120, 1680, 2460, 3380, 4210, 4680, 3920, 2850, 1960, 1320],
      release: [1320, 1680, 2080, 2360, 2480, 2320, 2050, 1720, 1380, 1080],
      level: [616.12, 616.20, 616.34, 616.58, 616.82, 616.76, 616.54, 616.28, 616.10, 615.94],
      area: [0.5, 0.9, 1.6, 2.7, 3.6, 3.3, 2.6, 1.7, 1.0, 0.5],
    },
    controlTarget: '优先降低库水位，快速腾出防洪库容',
    condition: '后续仍有强降雨、需提前腾库',
    expertComment: '这个方案加大下泄，为后续洪峰预留库容，水库侧风险最低，但龙门下游农田、低洼道路和施工区受影响更大，必须同步发布下游预警并做好人员转移准备。',
  },
}

export default {
  name: 'SmartOrb',
  inject: {
    voiceService: { from: 'voiceService', default: null },
    gestureService: { from: 'gestureService', default: null },
    llmService: { from: 'llmService', default: null },
    terminalSession: { from: 'terminalSession', default: null },
    assistantClient: { from: 'assistantClient', default: null },
  },
  data() {
    return {
      orbState: 'offline',
      panelOpen: false,
      chatHistory: [
        { role: 'ai', text: '你好，我是古贤水利智能助手。点击浮球或握拳唤醒我。' }
      ],
      textInput: '',
      isListening: false,
      isThinking: false,
      lastSmartOrbInput: '',
      pendingWorkflow: null,
      recentWorkflow: null,
      knowledgePortalActive: false,
      isKnowledgeOrbFlying: false,
      knowledgeOrbFlightStyle: {},
      knowledgeOrbFlightVariant: 'knowledge',
      smartOrbDockedPanelKey: null,
      _openPanelTimer: null,
      _finishPanelFlightTimer: null,
      suppressIdleBubble: false,
      activePromptIndex: 0,
      orbPosition: null,
      isDraggingOrb: false,
      dragStart: null,
      dragMoved: false,
      _stateMachine: null,
      _stateUnsubscribe: null,
      _terminalUnsubscribe: null,
      _gestureActionHandler: null,
      _listeningRequestId: 0,
      isListeningStarting: false,
      _interimText: '',
      _glowTimer: null,
      _bubbleTimer: null,
      _multimodalUnsubscribe: null,
      _activeTargetedQuery: null,
      multimodalState: multimodalContext.snapshot(),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      quickActions: [
        { label: '演示导览', prompt: '打开演示导览' },
        { label: '洪水演进', prompt: '洪水演进' },
        { label: '供需调度', prompt: '按供需缺口生成调度方案' },
        { label: '预警预案', prompt: '按预警启动防汛预案' },
      ],
    }
  },
  computed: {
    orbStateClass() {
      if (this.orbState === 'connecting' || this.orbState === 'onboarding') return 'idle'
      return this.orbState
    },
    orbSizeStyle() {
      const colors = STATE_COLORS[this.orbState] || STATE_COLORS.idle
      return {
        width: '96px', height: '96px',
        '--c1': colors.c1,
        '--c2': colors.c2,
        '--c3': colors.c3,
      }
    },
    showIdleBubble() {
      return !this.suppressIdleBubble && !this.panelOpen && !this.businessPanelOpen && !this.isKnowledgeOrbFlying && !this.isThinking && !this.isListening && !this.isDraggingOrb && this.orbState !== 'offline'
    },
    idlePromptText() {
      return IDLE_PROMPTS[this.activePromptIndex % IDLE_PROMPTS.length]
    },
    businessPanelOpen() {
      return !!panelStore.currentKey
    },
    businessPanelKey() {
      return panelStore.currentKey
    },
    knowledgePanelOpen() {
      return panelStore.currentKey === 'knowledgeQA'
    },
    smartOrbDockedPanelOpen() {
      return !!this.smartOrbDockedPanelKey && panelStore.currentKey === this.smartOrbDockedPanelKey
    },
    compactOrb() {
      return this.businessPanelOpen && !this.panelOpen
    },
    orbRootClass() {
      return {
        'is-dragging': this.isDraggingOrb,
        'is-compact': this.compactOrb,
        'has-business-panel': this.businessPanelOpen,
        'is-knowledge-launching': this.isKnowledgeOrbFlying,
        'is-knowledge-docked': this.smartOrbDockedPanelOpen && !this.panelOpen,
        'chat-open': this.panelOpen,
      }
    },
    knowledgeOrbFlightVariantClass() {
      return `flight-${this.knowledgeOrbFlightVariant || 'knowledge'}`
    },
    orbRootStyle() {
      if (this.orbPosition) {
        return {
          left: `${this.orbPosition.x}px`,
          top: `${this.orbPosition.y}px`,
          right: 'auto',
          bottom: 'auto',
        }
      }
      return {}
    },
    rootScreenBox() {
      const width = 112
      const height = 124
      if (this.orbPosition) {
        return { left: this.orbPosition.x, top: this.orbPosition.y, width, height }
      }
      const right = this.businessPanelOpen ? 18 : 32
      const bottom = this.businessPanelOpen ? 18 : 26
      return {
        left: this.viewportWidth - width - right,
        top: this.viewportHeight - height - bottom,
        width,
        height,
      }
    },
    dialogPanelStyle() {
      const panelWidth = Math.min(380, Math.max(300, this.viewportWidth - 28))
      const margin = 14
      const root = this.rootScreenBox
      const preferredLeft = root.left + root.width - panelWidth
      const clampedLeft = Math.min(Math.max(preferredLeft, margin), this.viewportWidth - panelWidth - margin)
      return {
        width: `${panelWidth}px`,
        left: `${clampedLeft - root.left}px`,
        right: 'auto',
      }
    },
    idleBubbleStyle() {
      const bubbleWidth = Math.min(300, Math.max(220, this.viewportWidth - 150))
      const margin = 14
      const root = this.rootScreenBox
      const preferredLeft = root.left + root.width - bubbleWidth
      const clampedLeft = Math.min(Math.max(preferredLeft, margin), this.viewportWidth - bubbleWidth - margin)
      return {
        maxWidth: `${bubbleWidth}px`,
        left: `${clampedLeft - root.left}px`,
        right: 'auto',
      }
    }
  },
  watch: {
    businessPanelOpen(open) {
      if (!open) {
        this.smartOrbDockedPanelKey = null
      }
    },
    businessPanelKey(key) {
      if (this.smartOrbDockedPanelKey && key && key !== this.smartOrbDockedPanelKey) {
        this.smartOrbDockedPanelKey = null
      }
    }
  },
  mounted() {
    this._stateMachine = ConversationStateMachine
    this._stateUnsubscribe = this._stateMachine.on('stateChange', ({ to }) => {
      this.orbState = to
    })
    this._setupVoiceCallbacks()
    this._setupGestureCallbacks()
    this._setupTerminalCallbacks()
    this._multimodalUnsubscribe = multimodalContext.subscribe((state) => {
      this.multimodalState = state
    })

    console.log('[SmartOrb] inject check:', {
      voiceService: !!this.voiceService,
      gestureService: !!this.gestureService,
      llmService: !!this.llmService,
      terminalSession: !!this.terminalSession,
    })

    this._bubbleTimer = setInterval(() => {
      if (this.showIdleBubble) {
        this.activePromptIndex = (this.activePromptIndex + 1) % IDLE_PROMPTS.length
      }
    }, 5200)
    window.addEventListener('mousemove', this.onDragMove)
    window.addEventListener('mouseup', this.endDrag)
    window.addEventListener('touchmove', this.onDragMove, { passive: false })
    window.addEventListener('touchend', this.endDrag)
    window.addEventListener('resize', this.onViewportResize)
  },
  methods: {
    _setupTerminalCallbacks() {
      if (!this.terminalSession) {
        this.orbState = 'offline'
        return
      }
      this._terminalUnsubscribe = this.terminalSession.subscribe('status', status => {
        this._syncTerminalConnection(status)
      })
      this._syncTerminalConnection(this.terminalSession.getStatus())
    },
    _syncTerminalConnection(status = {}) {
      const controlState = status.control || 'offline'
      const connected = controlState === 'connected'
        && Boolean(status.freshness && status.freshness.control && status.freshness.control.fresh)

      if (connected) {
        if (this._stateMachine) {
          if (this._stateMachine.isOffline()) this._stateMachine.connect()
          if (this._stateMachine.getState() === 'connecting') this._stateMachine.onboard()
          if (this._stateMachine.getState() === 'onboarding') this._stateMachine.ready()
        }
        if (!this.isListening && !this.isThinking && !this.isKnowledgeOrbFlying
          && ['offline', 'connecting', 'onboarding'].includes(this.orbState)) {
          this.orbState = 'idle'
        }
        return
      }

      this._listeningRequestId += 1
      this.isListeningStarting = false
      if (this.isListening && this.voiceService) this.voiceService.stopListening()
      this.isListening = false
      if (this._stateMachine && !this._stateMachine.isOffline()) this._stateMachine.goOffline()

      if (controlState === 'connecting' || controlState === 'reconnecting') {
        if (this._stateMachine && this._stateMachine.isOffline()) this._stateMachine.connect()
        this.orbState = 'connecting'
      } else {
        this.orbState = 'offline'
      }
    },
    _returnToConnectedIdle() {
      const connected = this.terminalSession
        && this.terminalSession.getStatus().control === 'connected'
        && this.terminalSession.getStatus().freshness.control.fresh
      if (!connected) {
        this.orbState = 'offline'
        return
      }
      if (this._stateMachine) {
        if (this._stateMachine.isOffline()) this._stateMachine.connect()
        if (this._stateMachine.getState() === 'connecting') this._stateMachine.onboard()
        if (this._stateMachine.getState() === 'onboarding') this._stateMachine.ready()
        if (!this._stateMachine.isIdle()) {
          this._stateMachine.backToIdle({ reason: 'interaction-finished' })
        }
      }
      this.orbState = 'idle'
    },
    onViewportResize() {
      this.viewportWidth = window.innerWidth
      this.viewportHeight = window.innerHeight
      this.ensureOrbInBounds()
    },
    _setupVoiceCallbacks() {
      if (!this.voiceService) return
      this.voiceService.onRecognized((text) => {
        text = this._normalizeInputText(text)
        this._interimText = text
        const last = this.chatHistory[this.chatHistory.length - 1]
        if (last && last.role === 'user') this.chatHistory.pop()
        this.chatHistory.push({ role: 'user', text })
        this.$nextTick(() => this._scrollChat())
      })
      this.voiceService.onSubmit((text) => {
        text = this._normalizeInputText(text)
        const targetedQuery = multimodalContext.resolveVoice(text)
        this._interimText = ''
        const last = this.chatHistory[this.chatHistory.length - 1]
        if (last && last.role === 'user') this.chatHistory.pop()
        this.chatHistory.push({ role: 'user', text })
        if (isCompetitionRuntimeEnabled()) {
          // Production mode: VoiceService is input-only.  The final transcript
          // enters the shared server-side FusionEngine/TaskManager exactly once.
          if (this.assistantClient) {
            if (targetedQuery) text = targetedQuery.prompt
            this.assistantClient.reportSpeechTranscript(text, true, 'zh')
          }
          return
        }
        // Rollback mode retains the complete legacy router/LLM business chain.
        this._activeTargetedQuery = targetedQuery
        this._processInput(text)
      })
      this.voiceService.onCommand((methodName) => {
        if (methodName === 'exitSystem') {
          this.panelOpen = false
        } else {
          this.$emit('command', methodName)
        }
        setTimeout(() => { this._returnToConnectedIdle() }, 500)
      })
      // 当 STT 自动停止（识别完成/连接关闭）时同步 SmartOrb 的 isListening 状态，
      // 否则第二次握拳手势会因 isListening 仍为 true 而被忽略。
      this.voiceService.onStopped(() => {
        this._listeningRequestId += 1
        this.isListeningStarting = false
        if (this.isListening) {
          this.isListening = false
          this._returnToConnectedIdle()
          console.log('[SmartOrb] STT 自动停止，isListening 已同步为 false')
        }
      })
    },
    _setupGestureCallbacks() {
      if (!this.gestureService) return
      this._gestureActionHandler = (action) => {
        console.log('[SmartOrb] 收到手势动作:', action, { panelOpen: this.panelOpen, isListening: this.isListening })
        const connectionReady = this.terminalSession
          && this.terminalSession.getStatus().control === 'connected'
          && this.terminalSession.getStatus().freshness.control.fresh
        const voiceActions = ['fist_hold', 'voice_toggle', 'voice_activate']
        // 退出手势已经经过双手+长按双重门控，必须能在思考/回答卡住时中断界面。
        const guardedActions = [...voiceActions, 'bye', 'confirm', 'confirm_send']
        if (!connectionReady && voiceActions.includes(action)) return
        if ((this.isThinking || ['thinking', 'answering', 'rendering'].includes(this.orbState))
          && guardedActions.includes(action)) {
          console.info('[SmartOrb] 当前对话阶段忽略高影响手势:', action)
          return
        }
        switch (action) {
          case 'fist_hold':
            // 握拳长按 → 打开面板并开始语音监听
            if (!this.panelOpen) {
              this.panelOpen = true
              this.$nextTick(() => {
                if (!this.isListening) this.toggleListening()
              })
            } else if (!this.isListening) {
              this.toggleListening()
            }
            // 如果正在 listening，不做操作（避免误关闭）
            break
          case 'voice_toggle':
            // V字手势 → 切换语音监听
            if (this.panelOpen) {
              this.toggleListening()
            } else {
              this.panelOpen = true
              this.$nextTick(() => {
                if (!this.isListening) this.toggleListening()
              })
            }
            break
          case 'voice_activate':
            // 双手(握拳+张开) → 语音激活
            if (!this.panelOpen) this.panelOpen = true
            this.$nextTick(() => {
              if (!this.isListening) this.toggleListening()
            })
            break
          case 'confirm_send':
            // 大拇指朝上 → 确认发送当前输入
            if (this.panelOpen) {
              this._cancelListeningStart()
              // 如果有文字输入则发送
              if (this.textInput && this.textInput.trim()) {
                this.handleTextSubmit()
              }
            }
            break
          case 'confirm':
            // OK手势 → 确认
            if (this.panelOpen && this.textInput && this.textInput.trim()) {
              this.handleTextSubmit()
            }
            break
          case 'bye':
            // 挥手 → 返回/关闭
            if (this.panelOpen) {
              this.closeChatPanel()
            } else if (panelStore.currentKey) {
              panelStore.close()
            }
            break
          case 'exit_system':
            // 双手握拳 → 退出所有面板
            this.panelOpen = false
            this._cancelListeningStart()
            panelStore.close()
            panelStore.setLeftBoard(false)
            panelStore.setRightBoard(false)
            this._returnToConnectedIdle()
            break
          case 'scale_drag':
            // 双手捏合 → 缩放/拖拽 (暂时只做日志)
            console.log('[SmartOrb] 双手缩放拖拽手势')
            break
        }
      }
      this.gestureService.onAction(this._gestureActionHandler)
    },

    async _processInput(text, targetedQuery = this._activeTargetedQuery) {
      this._activeTargetedQuery = null
      text = this._normalizeInputText(text)
      if (targetedQuery) {
        multimodalContext.setVoiceStatus('query_running', { targetedQuery })
      }
      this.lastSmartOrbInput = text
      this.orbState = 'thinking'
      this.isThinking = true
      this.$nextTick(() => this._scrollChat())

      // 0. 多模态目标指代消歧与预测意图优先处理
      const multiSnapshot = multimodalContext.snapshot()
      const targeted = targetedQuery || multiSnapshot.voice.targetedQuery || multiSnapshot.voice.targetedIntent

      // 0.1 解除锁定
      if (targeted && targeted.intent === 'target.unlock') {
        this._pushAssistant('已为您解除水库目标锁定。')
        const app = this.$parent
        if (app && typeof app._unlockReservoirTarget === 'function') {
          app._unlockReservoirTarget()
        }
        this.isThinking = false
        this._pollSpeakingState()
        return
      }

      // 0.2 未锁定目标但使用了指代词
      if (targeted && targeted.error === 'NO_TARGET_LOCKED') {
        this._pushAssistant('请先指向并锁定目标水库。')
        this.isThinking = false
        this._pollSpeakingState()
        return
      }

      // 0.3 流量预测任务创建
      if (targeted && targeted.intent === 'reservoir.forecast' && targeted.target) {
        const target = targeted.target
        const targetName = target.displayName || target.name || '水库'
        const horizon = (targeted.parameters && targeted.parameters.horizon) || 24
        
        // 创建真实 Forecast 任务结构
        const forecastTask = createForecastTaskForReservoir(target, {
          initialQuery: text,
          horizonHours: horizon,
          source: 'multimodal',
        })
        
        // 记录到多模态上下文并广播给 TaskGraphPanel
        multimodalContext.recordTaskCreated(forecastTask)
        this.$root.$emit('task-graph-update', forecastTask)
        if (this.assistantClient && typeof this.assistantClient.reportTaskGraph === 'function') {
          this.assistantClient.reportTaskGraph(forecastTask)
        }

        const reply = `已为您创建【${targetName}流量预测】任务（任务ID: ${forecastTask.rootTaskId}）。系统已将“这个水库”解析为${targetName}，完成意图解析与业务Context绑定，正等待模型推理。`
        
        this._pushAssistant(reply)
        this.isThinking = false
        this._pollSpeakingState()
        return
      }

      if (this._isMainScreenIntent(text)) {
        this.pendingWorkflow = null
        panelStore.close()
        panelStore.setLeftBoard(false)
        panelStore.setRightBoard(false)
        // 清理3D地球上的洪水实体和应急图层
        this._cleanupAllGisOverlays()
        // 飞回初始俯瞰视角
        this._flyToHomeView()
        this._pushAssistant('已关闭当前面板并返回主界面。你可以继续让我打开综合监控、洪水演进、供水分析或应急管理。')
        this.isThinking = false
        this._pollSpeakingState()
        return
      }

      const reservoirAnswer = this._tryReservoirParameterQuestion(text)
      if (reservoirAnswer) {
        this.isThinking = false
        this._pollSpeakingState()
        return
      }

      if (this.pendingWorkflow && this._startsNewBusinessTask(text)) {
        this.pendingWorkflow = null
      }

      if (this.pendingWorkflow) {
        const continued = await this._continueWorkflow(text)
        if (continued) {
          this.isThinking = false
          this._pollSpeakingState()
          return
        }
      }

      if (this._isConfirmation(text)) {
        const continued = await this._continueRecentWorkflow()
        if (continued) {
          this.isThinking = false
          this._pollSpeakingState()
          return
        }
      }

      const workflowHandled = await this._tryBusinessWorkflow(text)
      if (workflowHandled) {
        this.isThinking = false
        this._pollSpeakingState()
        return
      }

      // 知识问答检测必须在关键词路由之前执行
      // 否则"防渗"会被模糊匹配为"防汛"→应急面板，"泄洪"会被匹配为"洪水"→洪水预演
      if (this._isKnowledgeDesignIntent(text)) {
        const openOnly = /打开|进入|知识问答|问答面板|AI交互/.test(text) && text.length <= 12
        this._openKnowledgePanel({
          source: 'smartOrb',
          initialQuery: openOnly ? '' : text,
        })
        this._pushAssistant(openOnly
          ? '已打开工程设计知识问答。你可以继续问设计规范、抗滑稳定、安全系数、导流、防渗、泄洪消能等问题。'
          : '已交给工程设计知识问答面板处理。我会播报简要结论，详细依据和报告草稿请在面板中查看。')
        this.isThinking = false
        this._pollSpeakingState()
        return
      }

      const matched = this._tryKeywordMatch(text)
      if (matched) {
        this.isThinking = false
        this._pollSpeakingState()
        return
      }

      const dataAnswer = await this._tryMonitoringQuestion(text)
      if (dataAnswer) {
        this.isThinking = false
        this._pollSpeakingState()
        return
      }

      console.log('[SmartOrb] No keyword match, llmService:', !!this.llmService)

      if (this.llmService) {
        try {
          console.log('[SmartOrb] Calling LLM with tools...')
          const result = await this.llmService.ask(
            targetedQuery ? targetedQuery.prompt : text,
            this._getConversationContext()
          )
          console.log('[SmartOrb] LLM result text:', result.text?.slice(0, 80))
          console.log('[SmartOrb] LLM actions:', result.actions)

          this.isThinking = false
          this.chatHistory.push({ role: 'ai', text: result.text })
          this.$nextTick(() => this._scrollChat())

          if (result.actions && result.actions.length > 0) {
            let lastPanelAction = null
            for (const action of result.actions) {
              if (action === 'leftBoard') {
                panelStore.setLeftBoard(true)
              } else if (action === 'rightBoard') {
                panelStore.setRightBoard(true)
              } else if (action === 'mulSchemes') {
                // mulSchemes 面板由 CesiumScene 的 mulSchemesVisible 控制，不走 panelStore
                const reservoir = findReservoirByName(this.lastSmartOrbInput || '')
                const isExplicitReservoir = reservoir && (
                  reservoir.aliases.some(a => (this.lastSmartOrbInput || '').includes(a)) ||
                  (this.lastSmartOrbInput || '').includes(reservoir.name)
                )
                panelStore.close()
                panelStore.setLeftBoard(false)
                panelStore.setRightBoard(false)
                this.panelOpen = false
                this.suppressIdleBubble = true
                if (isExplicitReservoir) {
                  this._flyToReservoir(reservoir)
                }
                const app = this.$parent
                const cesiumScene = app && app.$refs ? app.$refs.cesiumScene : null
                if (cesiumScene && cesiumScene.changeScheme) {
                  const reservoirName = isExplicitReservoir ? reservoir.name : null
                  cesiumScene.changeScheme(2, reservoirName)
                }
              } else {
                lastPanelAction = action
              }
            }
            if (lastPanelAction) this._openBusinessPanel(lastPanelAction)
          }

          this._speakAndAnimate(result.text)
        } catch (e) {
          console.error('[SmartOrb] LLM error:', e)
          this.isThinking = false
          this.chatHistory.push({ role: 'ai', text: this._formatLLMError(e) })
          this._returnToConnectedIdle()
        }
      } else {
        console.warn('[SmartOrb] llmService is null, inject failed')
        this.isThinking = false
        this.chatHistory.push({ role: 'ai', text: `你好！我收到了：${text}。LLM服务未连接，请检查配置。` })
        this._returnToConnectedIdle()
      }
    },

    useIdlePrompt() {
      const prompt = this.idlePromptText
      this.panelOpen = true
      this.chatHistory.push({ role: 'user', text: prompt })
      this._processInput(prompt)
    },

    runQuickAction(action) {
      if (!action || !action.prompt || this.isThinking) return
      this.panelOpen = true
      this.chatHistory.push({ role: 'user', text: action.prompt })
      this._processInput(action.prompt)
    },

    startDrag(event) {
      const point = this._pointerPoint(event)
      const rect = this._getOrbRect()
      this.dragStart = {
        pointerX: point.x,
        pointerY: point.y,
        orbX: rect.left,
        orbY: rect.top,
      }
      this.isDraggingOrb = true
      this.dragMoved = false
    },

    onDragMove(event) {
      if (!this.isDraggingOrb || !this.dragStart) return
      if (event.cancelable) event.preventDefault()
      const point = this._pointerPoint(event)
      const dx = point.x - this.dragStart.pointerX
      const dy = point.y - this.dragStart.pointerY
      if (Math.abs(dx) + Math.abs(dy) > 6) this.dragMoved = true
      const nextOrb = this._clampOrbBox({
        x: this.dragStart.orbX + dx,
        y: this.dragStart.orbY + dy,
      })
      this.orbPosition = this._rootPositionFromOrbBox(nextOrb)
    },

    endDrag() {
      if (!this.isDraggingOrb) return
      this.isDraggingOrb = false
      this.dragStart = null
      if (this.orbPosition) this.orbPosition = this._dockRootPosition()
      setTimeout(() => { this.dragMoved = false }, 0)
    },

    _pointerPoint(event) {
      const source = event.touches && event.touches.length ? event.touches[0] : event
      return { x: source.clientX, y: source.clientY }
    },

    _getOrbRect() {
      const orb = this.$el.querySelector('.orb-wrapper')
      return orb ? orb.getBoundingClientRect() : this.$el.getBoundingClientRect()
    },

    _getRootToOrbOffset() {
      const root = this.$el.getBoundingClientRect()
      const orb = this._getOrbRect()
      return {
        x: orb.left - root.left,
        y: orb.top - root.top,
      }
    },

    _clampOrbBox(pos) {
      const orb = this._getOrbRect()
      const width = Math.min(Math.max(orb.width || 112, 72), 140)
      const height = Math.min(Math.max(orb.height || 112, 72), 150)
      const margin = 12
      const maxX = Math.max(margin, window.innerWidth - width - margin)
      const maxY = Math.max(margin, window.innerHeight - height - margin)
      return {
        x: Math.min(Math.max(pos.x, margin), maxX),
        y: Math.min(Math.max(pos.y, margin), maxY),
      }
    },

    _rootPositionFromOrbBox(orbBox) {
      const offset = this._getRootToOrbOffset()
      return this._clampRootPosition({
        x: orbBox.x - offset.x,
        y: orbBox.y - offset.y,
      })
    },

    _clampRootPosition(pos) {
      const root = this.$el.getBoundingClientRect()
      const width = Math.min(root.width || 120, Math.max(120, window.innerWidth - 24))
      const height = Math.min(root.height || 120, Math.max(120, window.innerHeight - 24))
      const margin = 8
      const maxX = Math.max(margin, window.innerWidth - width - margin)
      const maxY = Math.max(margin, window.innerHeight - height - margin)
      const x = Number.isFinite(pos.x) ? pos.x : maxX
      const y = Number.isFinite(pos.y) ? pos.y : maxY
      return {
        x: Math.min(Math.max(x, margin), maxX),
        y: Math.min(Math.max(y, margin), maxY),
      }
    },

    _dockRootPosition() {
      const orb = this._getOrbRect()
      const dockedOrbX = orb.left + orb.width / 2 < window.innerWidth / 2
        ? 18
        : window.innerWidth - Math.min(Math.max(orb.width || 112, 72), 140) - 18
      return this._rootPositionFromOrbBox(this._clampOrbBox({ x: dockedOrbX, y: orb.top }))
    },

    ensureOrbInBounds() {
      if (!this.orbPosition) return
      const orb = this._getOrbRect()
      const outOfBounds = orb.right < 24 || orb.left > window.innerWidth - 24 || orb.bottom < 24 || orb.top > window.innerHeight - 24
      if (outOfBounds) {
        this.orbPosition = null
        return
      }
      this.orbPosition = this._rootPositionFromOrbBox(this._clampOrbBox({ x: orb.left, y: orb.top }))
    },

    _tryKeywordMatch(text) {
      // 提前检测用户是否指定了特定应急场景，在路由匹配前切换
      this._detectAndSwitchScenario(text)
      const intent = routePanelIntent(text)
      if (!intent) return false

      if (intent.method === 'exitSystem') {
        this.pendingWorkflow = null
        this.panelOpen = false
        panelStore.close()
        panelStore.setLeftBoard(false)
        panelStore.setRightBoard(false)
        // 清理3D地球上的洪水实体和应急图层
        this._cleanupAllGisOverlays()
        // 飞回初始俯瞰视角
        this._flyToHomeView()
        this.chatHistory.push({ role: 'ai', text: '已关闭当前面板并返回主界面。' })
        this._speakAndAnimate('已关闭当前面板并返回主界面。')
        return true
      }

      this.chatHistory.push({ role: 'ai', text: intent.reply })
      // 重要：不能同时调用 _openBusinessPanel 和 $emit('command')，
      // 否则 panelStore.open() 会被触发两次，CesiumScene 的 watcher
      // 会重复执行 renderEmergencyGisLayer + camera.flyTo，导致画面跳来跳去。
      //
      // 有 method 的面板：走 CesiumScene 方法（$emit command → App.vue → scene[method]），
      //   CesiumScene 方法内部会调用 panelStore.open() 并处理相机飞行。
      // 无 method 的面板：通过 _openBusinessPanel 打开（含 orb 飞行动画）。
      if (intent.method) {
        this.$emit('command', intent.method)
      } else if (intent.panelKey === 'mulSchemes') {
        // mulSchemes 面板由 CesiumScene 的 mulSchemesVisible 控制，不走 panelStore
        // 检测用户是否提到了具体水库（如"薛峰来水预测方案"）
        const reservoir = findReservoirByName(this.lastSmartOrbInput || '')
        const isExplicitReservoir = reservoir && (
          reservoir.aliases.some(a => (this.lastSmartOrbInput || '').includes(a)) ||
          (this.lastSmartOrbInput || '').includes(reservoir.name)
        )
        panelStore.close()
        panelStore.setLeftBoard(false)
        panelStore.setRightBoard(false)
        this.panelOpen = false
        this.suppressIdleBubble = true
        // 如果提到了具体水库，飞行到该水库位置
        if (isExplicitReservoir) {
          this._flyToReservoir(reservoir)
        }
        const app = this.$parent
        const cesiumScene = app && app.$refs ? app.$refs.cesiumScene : null
        if (cesiumScene && cesiumScene.changeScheme) {
          const reservoirName = isExplicitReservoir ? reservoir.name : null
          cesiumScene.changeScheme(2, reservoirName)
        }
      } else if (intent.panelKey && intent.panelKey !== '__exit__') {
        this._openBusinessPanel(intent.panelKey)
      }
      if (intent.reply) this._speakAndAnimate(intent.reply)
      return true
    },

    _isKnowledgeDesignIntent(text) {
      return /(知识问答|工程设计|设计规范|设计标准|设计审查|生成报告|规范符合性|计算书|重力坝|抗滑|安全系数|坝基|防渗|导流|泄洪|消能|边坡|生态流量)/.test(text)
    },

    _getPanelFlightTarget(key) {
      const marginX = Math.max(28, Math.round(window.innerWidth * 0.035))
      const marginY = Math.max(26, Math.round(window.innerHeight * 0.04))
      const centeredWidth = Math.min(1200, window.innerWidth - marginX * 2)
      const centeredHeight = Math.min(700, window.innerHeight - marginY * 2)
      const centeredLeft = Math.round((window.innerWidth - centeredWidth) / 2)
      const centeredTop = Math.round((window.innerHeight - centeredHeight) / 2)

      if (key === 'supplyDemand') {
        return {
          x: Math.round(window.innerWidth / 2 - 37),
          y: Math.round(window.innerHeight - 145),
          variant: 'sheet',
        }
      }
      if (key === 'custom') {
        return {
          x: Math.round(window.innerWidth - Math.min(520, window.innerWidth * 0.38) - 12),
          y: Math.round(window.innerHeight * 0.18),
          variant: 'dock',
        }
      }
      if (key === 'floodSim') {
        return {
          x: Math.round(window.innerWidth * 0.18),
          y: Math.round(window.innerHeight * 0.16),
          variant: 'flood',
        }
      }
      if (key === 'storyline') {
        return {
          x: Math.round(centeredLeft + centeredWidth * 0.5 - 37),
          y: Math.round(centeredTop + 20),
          variant: 'storyline',
        }
      }
      if (key === 'emergency' || key === 'monitoring' || key === 'notice') {
        return {
          x: Math.round(centeredLeft + 26),
          y: Math.round(centeredTop + 18),
          variant: 'alert',
        }
      }
      if (key === 'scheduling' || key === 'construction' || key === 'project' || key === 'maintenance' || key === 'waterSupply') {
        return {
          x: Math.round(centeredLeft + centeredWidth - 104),
          y: Math.round(centeredTop + 22),
          variant: 'dock',
        }
      }
      return {
        x: Math.round(centeredLeft + 22),
        y: Math.round(centeredTop + 15),
        variant: 'knowledge',
      }
    },

    _buildPanelFlightStyle(rect, target, variant) {
      const startX = Math.round(rect.left)
      const startY = Math.round(rect.top)
      const targetX = target.x
      const targetY = target.y
      const startWidth = Math.round(rect.width || 96)
      const targetSize = variant === 'sheet' ? 82 : variant === 'alert' ? 88 : 74
      const toScale = targetSize / Math.max(1, startWidth)
      const dx = targetX - startX
      const dy = targetY - startY
      const presets = {
        knowledge: {
          mid1: [0.36, -96],
          mid2: [0.72, -18],
          trailA: 'rgba(255, 78, 205, 0.34)',
          trailB: 'rgba(37, 217, 255, 0.28)',
          midScale: 0.86,
          settleScale: 1.28,
          endScale: 0.72,
          rotate1: '-8deg',
          rotate2: '5deg',
          duration: '1.12s',
        },
        storyline: {
          mid1: [0.22, -180],
          mid2: [0.66, -104],
          trailA: 'rgba(124, 60, 255, 0.42)',
          trailB: 'rgba(64, 255, 186, 0.3)',
          midScale: 1.08,
          settleScale: 1.42,
          endScale: 0.84,
          rotate1: '-18deg',
          rotate2: '10deg',
          duration: '1.18s',
        },
        flood: {
          mid1: [0.18, 92],
          mid2: [0.62, -72],
          trailA: 'rgba(37, 217, 255, 0.42)',
          trailB: 'rgba(49, 112, 255, 0.32)',
          midScale: 0.78,
          settleScale: 1.2,
          endScale: 0.7,
          rotate1: '22deg',
          rotate2: '-14deg',
          duration: '1.24s',
        },
        sheet: {
          mid1: [0.45, 58],
          mid2: [0.72, 20],
          trailA: 'rgba(64, 255, 186, 0.38)',
          trailB: 'rgba(248, 255, 89, 0.28)',
          midScale: 0.92,
          settleScale: 1.48,
          endScale: 0.9,
          rotate1: '8deg',
          rotate2: '0deg',
          duration: '1.02s',
        },
        alert: {
          mid1: [0.35, -48],
          mid2: [0.63, -118],
          trailA: 'rgba(255, 138, 61, 0.44)',
          trailB: 'rgba(255, 78, 205, 0.3)',
          midScale: 0.72,
          settleScale: 1.62,
          endScale: 0.62,
          rotate1: '-26deg',
          rotate2: '18deg',
          duration: '0.96s',
        },
        dock: {
          mid1: [0.28, -46],
          mid2: [0.76, -22],
          trailA: 'rgba(124, 60, 255, 0.38)',
          trailB: 'rgba(37, 217, 255, 0.34)',
          midScale: 0.82,
          settleScale: 1.22,
          endScale: 0.74,
          rotate1: '-10deg',
          rotate2: '-6deg',
          duration: '1.08s',
        },
      }
      const preset = presets[variant] || presets.knowledge
      const mid1X = Math.round(startX + dx * preset.mid1[0])
      const mid1Y = Math.round(startY + dy * preset.mid1[0] + preset.mid1[1])
      const mid2X = Math.round(startX + dx * preset.mid2[0])
      const mid2Y = Math.round(startY + dy * preset.mid2[0] + preset.mid2[1])
      return {
        '--from-x': `${startX}px`,
        '--from-y': `${startY}px`,
        '--from-size': `${startWidth}px`,
        '--mid-x': `${mid1X}px`,
        '--mid-y': `${mid1Y}px`,
        '--mid2-x': `${mid2X}px`,
        '--mid2-y': `${mid2Y}px`,
        '--to-x': `${targetX}px`,
        '--to-y': `${targetY}px`,
        '--to-size': `${targetSize}px`,
        '--to-scale': `${toScale}`,
        '--mid-scale': preset.midScale,
        '--settle-scale': preset.settleScale,
        '--end-scale': preset.endScale,
        '--rotate-mid': preset.rotate1,
        '--rotate-settle': preset.rotate2,
        '--trail-a': preset.trailA,
        '--trail-b': preset.trailB,
        '--flight-duration': preset.duration,
      }
    },

    _canonicalPanelKey(key) {
      const aliases = {
        showComprehensiveMonitoring: 'monitoring',
        showWaterSupplyScale: 'waterSupply',
        showWaterDistributionLines: 'scheduling',
        handleSmartConstruction: 'construction',
        handleMaintenance: 'maintenance',
        handleEmergency: 'emergency',
        handleProjectManagement: 'project',
        handleOperationDispatch: 'notice',
        reservoir: 'reservoirInfo',
        regulating: 'regulatingReservoir',
      }
      return aliases[key] || key
    },

    _openBusinessPanel(key, props = {}, options = {}) {
      if (!key) return
      const panelKey = this._canonicalPanelKey(key)
      const initialQuery = String(props.initialQuery || options.initialQuery || this.lastSmartOrbInput || '').trim()
      const handoffProps = {
        ...props,
        source: props.source || 'smartOrb',
        initialQuery,
        smartOrbHandoff: {
          source: props.source || 'smartOrb',
          panelKey,
          query: initialQuery,
          reason: props.reason || props.recommendedTask || props.sourceWarning || '',
          openedAt: Date.now(),
        },
      }
      this.knowledgePortalActive = false
      this.isKnowledgeOrbFlying = false
      this.panelOpen = false
      this.suppressIdleBubble = true
      window.clearTimeout(this._openPanelTimer)
      window.clearTimeout(this._finishPanelFlightTimer)
      if (this.isListening && this.voiceService) {
        this.voiceService.stopListening()
        this.isListening = false
      }
      this.$nextTick(() => {
        const orb = this.$el.querySelector('.siri-orb')
        const rect = orb ? orb.getBoundingClientRect() : { left: window.innerWidth - 128, top: window.innerHeight - 128, width: 96, height: 96 }
        const target = this._getPanelFlightTarget(panelKey)
        const variant = options.variant || target.variant || 'knowledge'
        const variantOpenDelays = {
          knowledge: 760,
          storyline: 840,
          flood: 880,
          sheet: 690,
          alert: 650,
          dock: 730,
        }
        const variantFlightDurations = {
          knowledge: 1120,
          storyline: 1180,
          flood: 1240,
          sheet: 1020,
          alert: 960,
          dock: 1080,
        }
        const openDelay = options.openDelay || variantOpenDelays[variant] || 760
        const flightDuration = options.flightDuration || variantFlightDurations[variant] || 1120
        this.knowledgeOrbFlightVariant = variant
        this.knowledgeOrbFlightStyle = this._buildPanelFlightStyle(rect, target, variant)
        this.isKnowledgeOrbFlying = true
        this.orbState = 'answering'
        this._openPanelTimer = window.setTimeout(() => {
          this.smartOrbDockedPanelKey = panelKey
          panelStore.open(panelKey, handoffProps)
        }, openDelay)
        this._finishPanelFlightTimer = window.setTimeout(() => {
          this.knowledgePortalActive = false
          this.isKnowledgeOrbFlying = false
          if (!this.isThinking) this._pollSpeakingState()
        }, flightDuration)
      })
    },

    _openKnowledgePanel(props = {}) {
      this._openBusinessPanel('knowledgeQA', props)
    },

    async _tryBusinessWorkflow(text) {
      // 提前检测用户是否指定了特定应急场景
      this._detectAndSwitchScenario(text)
      if (this._isStorylineIntent(text)) {
        const story = this._storylineTargetFromText(text)
        const storyProps = typeof story.props === 'function' ? story.props() : (story.props || {})
        this._openBusinessPanel(story.key, storyProps)
        this.pendingWorkflow = null
        this._pushAssistant(story.reply)
        return true
      }

      if (this._isEmergencyPlanIntent(text)) {
        // 检测用户是否指定了特定场景
        if (/延长/.test(text)) {
          switchScenario('yanchang')
        } else if (/龙门|禹门口/.test(text)) {
          switchScenario('longmen')
        }
        const info = getActiveScenarioInfo()
        this._openBusinessPanel('emergency', {
          recommendedPlan: info.defaultPlanName,
          sourceWarning: '由当前预警和预演结果自动推荐',
          flyToEmergency: true,
        })
        this.pendingWorkflow = null
        this._pushAssistant(`已打开${info.name}防汛应急联动方案，已自动跳转至 GIS 界面。当前联动逻辑为：上游预警触发预演复核，预演结果再匹配人员转移、交通封控、避险安置、抢险队伍和物资前置。GIS 图层包含 1 条下泄影响走廊、3 个响应断面、5 个风险点、5 个避险点、4 个物资点、4 条转移路线和 4 个管控分区。`)
        return true
      }

      if (this._isSupplyDispatchIntent(text)) {
        this._openBusinessPanel('scheduling', {
          source: 'supplyDemand',
          recommendedTask: '供需缺口调度建议'
        })
        this.pendingWorkflow = null
        this._pushAssistant('已按供需缺口进入运行调度。建议采用“均衡调度”作为展示方案：优先保障城镇生活供水和生态基流，农业灌溉按分区缺口滚动补水，工业园区维持计划流量的95%以上。')
        return true
      }

      if (this._isFloodSchemeIntent(text)) {
        await this._executeFloodSchemeWorkflow(text)
        return true
      }

      if (this._isFloodIntent(text)) {
        const slots = this._parseFloodSlots(text, {})
        if (this._shouldAutoRunFlood(text, slots)) {
          await this._executeFloodWorkflow(slots)
          return true
        }
        this.pendingWorkflow = { type: 'flood', slots }
        const reply = this._buildFloodQuestion(slots)
        this._pushAssistant(reply)
        return true
      }

      if (this._isDispatchIntent(text)) {
        const slots = this._parseDispatchSlots(text, {})
        if (this._shouldAutoRunDispatch(text, slots)) {
          await this._executeDispatchWorkflow(slots)
          return true
        }
        this.pendingWorkflow = { type: 'dispatch', slots }
        const reply = this._buildDispatchQuestion(slots)
        this._pushAssistant(reply)
        return true
      }

      if (this._isProjectBriefIntent(text)) {
        await this._executeProjectBrief()
        return true
      }

      if (this._isSupplyIntent(text)) {
        const slots = this._parseSupplySlots(text, {})
        this.pendingWorkflow = { type: 'supply', slots }
        const reply = `我先按${slots.area || DEFAULT_WORKFLOW_SLOTS.supply.area}、${slots.period || DEFAULT_WORKFLOW_SLOTS.supply.period}做供需分析。你可以补充区域和时段，例如“韩城供水区，未来72小时”；也可以直接回复“确认”。`
        this._pushAssistant(reply)
        return true
      }

      return false
    },

    async _continueWorkflow(text) {
      if (this._isCancelWorkflow(text)) {
        this.pendingWorkflow = null
        this._pushAssistant('好的，当前连续任务已暂停。你可以继续问水位、降雨、调度、洪水演进或工程态势。')
        return true
      }

      const workflow = this.pendingWorkflow
      if (workflow.type === 'flood') {
        workflow.slots = this._parseFloodSlots(text, workflow.slots)
        if (this._isConfirmation(text) || this._hasFloodRequiredSlots(workflow.slots)) {
          await this._executeFloodWorkflow(workflow.slots)
          this.pendingWorkflow = null
          return true
        }
        this._pushAssistant(this._buildFloodQuestion(workflow.slots))
        return true
      }

      if (workflow.type === 'dispatch') {
        workflow.slots = this._parseDispatchSlots(text, workflow.slots)
        if (this._isConfirmation(text) || this._hasDispatchRequiredSlots(workflow.slots)) {
          await this._executeDispatchWorkflow(workflow.slots)
          if (this.pendingWorkflow === workflow) this.pendingWorkflow = null
          return true
        }
        this._pushAssistant(this._buildDispatchQuestion(workflow.slots))
        return true
      }

      if (workflow.type === 'briefing') {
        await this._executeBriefingFollowUp(text)
        if (this.pendingWorkflow === workflow) this.pendingWorkflow = null
        return true
      }

      if (workflow.type === 'supply') {
        workflow.slots = this._parseSupplySlots(text, workflow.slots)
        if (this._isConfirmation(text) || workflow.slots.area || workflow.slots.period) {
          await this._executeSupplyWorkflow(workflow.slots)
          this.pendingWorkflow = null
          return true
        }
        this._pushAssistant('请补充分析范围或时段，例如“韩城供水区，未来72小时”；也可以回复“确认”，我会按统一供需 API 的当前汇总继续。')
        return true
      }

      this.pendingWorkflow = null
      return false
    },

    _detectAndSwitchScenario(text) {
      // 检测用户是否在语音中指定了特定应急场景
      if (/延长/.test(text)) {
        switchScenario('yanchang')
      } else if (/龙门|禹门口/.test(text)) {
        switchScenario('longmen')
      }
    },

    _isStorylineIntent(text) {
      return /(故事线|演示导览|演示模式|汇报模式|一键演示|讲解路线|业务主线)/.test(text)
    },

    _isEmergencyPlanIntent(text) {
      // 支持两种语序：
      // 1. "预案...打开/查看" (关键词在前，动词在后)
      // 2. "打开/查看...预案/应急" (动词在前，关键词在后)
      return /(预案|应急方案|防汛方案|启动应急|应急处置|抢险|下泄影响|应急联动|应急GIS|应急地图).*(打开|启动|推荐|联动|执行|进入|查看|跳转)|(打开|查看|启动|进入|跳转).*(预案|应急方案|防汛方案|应急预案|应急联动|应急GIS|应急地图)|按.*(预警|洪水|风险).*(预案|应急|GIS|地图)/.test(text)
    },

    _isSupplyDispatchIntent(text) {
      return /(供需|缺水|缺口|供水).*(调度|补水|配水|生成方案|生成建议)|按.*供需.*(调度|配水)/.test(text)
    },

    _storylineTargetFromText(text) {
      const stories = [
        {
          match: /(洪水|洪峰|演进|预演|淹没|龙门)/,
          key: 'floodSim',
          reply: '已进入洪水演进故事线。建议演示顺序为：选择洪水频率，采用下泄方案2进行预演，查看24小时过程线，再联动应急预案。'
        },
        {
          match: /(调度|下泄|方案比选|水库调度)/,
          key: 'custom',
          reply: '已进入水库调度故事线。当前可展示保守调度、均衡调度和泄洪优先三类方案，并继续联动洪水演进。'
        },
        {
          match: /(供需|供水|缺水|缺口|配水)/,
          key: 'supplyDemand',
          props: { workflow: { area: '韩城供水区', period: '未来24小时' } },
          reply: '已进入供需平衡故事线。面板会展示分区供需缺口，你可以继续说“按供需缺口生成调度方案”。'
        },
        {
          match: /(预警|预案|应急|抢险|防汛)/,
          key: 'emergency',
          props: () => {
            const info = getActiveScenarioInfo()
            return { recommendedPlan: info.defaultPlanName, sourceWarning: '由洪水预演结果自动联动' }
          },
          reply: '已进入预警预案故事线。这里可以展示风险研判、队伍物资和推荐预案，适合承接洪水预演后的处置汇报。'
        },
        {
          match: /(监控|监测|水位|雨情|水质|流量)/,
          key: 'monitoring',
          reply: '已进入综合监控故事线。这里适合先汇报水位、雨情、水质、入库出库流量，再进入预报预警。'
        },
        {
          match: /(工程总览|参数|库容|特征水位|枢纽)/,
          key: 'reservoirInfo',
          reply: '已进入工程总览故事线。可先汇报总库容、特征水位、装机容量和工程运行态势。'
        },
        {
          match: /(运维|工程管理|智能建造|通知|公告|业务管理)/,
          key: 'scheduling',
          props: { source: 'supplyDemand', recommendedTask: '故事线导览推荐查看供需保障与运行调度任务' },
          reply: '已进入业务管理故事线。建议展示运行任务、工程进度、运维工单、智能建造和通知公告的联动。'
        },
        {
          match: /(AI|问答|知识|助手|交互|专家)/i,
          key: 'knowledgeQA',
          reply: '已进入AI交互故事线。可以用自然语言查数据、开面板、发起模型联算和生成专家汇报。'
        }
      ]
      const found = stories.find(story => story.match.test(text))
      return found || {
        key: 'storyline',
        reply: '已打开演示导览。当前平台围绕“看得见、算得准、能推演、可对话”组织为八条故事线：工程总览、综合监控、洪水演进、水库调度、供需平衡、预警预案、业务管理和AI交互。你可以继续说“进入洪水演进故事线”或“打开供需平衡”。'
      }
    },

    _isFloodIntent(text) {
      // "洪水演进"单独出现即触发（不需要额外动词）
      if (/洪水演进|洪水预演|洪水模拟|洪演/.test(text)) return true
      return /(洪水|洪演|演进|淹没|淹没范围| flood)/i.test(text) && /(模拟|预演|推演|分析|现在|进行|做|开始|启动|计算|调度|均衡|保守|泄洪)/.test(text)
    },

    _isFloodSchemeIntent(text) {
      return /(下泄方案|泄洪方案|方案)\s*[一二三123]|[一二三123]\s*号?方案/.test(text) && /(预演|演进|模拟|推演|启动|开始|使用|采用|按)/.test(text)
    },

    _parseFloodSchemeNumber(text) {
      if (/方案\s*[一1]|[一1]\s*号?方案/.test(text)) return 1
      if (/方案\s*[二2]|[二2]\s*号?方案/.test(text)) return 2
      if (/方案\s*[三3]|[三3]\s*号?方案/.test(text)) return 3
      return 2
    },

    _schemeModeFromNumber(num) {
      return FLOOD_SCHEME_EXPERTISE[num] || FLOOD_SCHEME_EXPERTISE[2]
    },

    _schemeNumberFromDispatchMode(mode) {
      if (/保守/.test(mode)) return 1
      if (/泄洪优先/.test(mode)) return 3
      return 2
    },

    _isDispatchIntent(text) {
      if (/(洪水|洪演|演进|淹没)/.test(text)) return false
      return /(水库调度|运行调度|调度|下泄|泄洪|方案|预演)/.test(text) && /(进行|做|启动|对比|比选|计算|预演|保守|均衡|泄洪优先|固定|补偿|古贤水利枢纽)/.test(text)
    },

    _isProjectBriefIntent(text) {
      if (/(参数|属性|库容|正常蓄水位|死水位|汛限|装机|工程指标)/.test(text)) return false
      return /(工程|水库|当前|现在|整体|运行态势|态势|汇报)/.test(text) && /(态势|汇报|总览|整体|运行情况)/.test(text)
    },

    _isSupplyIntent(text) {
      return /(供需|供水|需水|缺口|平衡)/.test(text) && /(分析|缺口|预警|看看|测算|汇报)/.test(text)
    },

    _isConfirmation(text) {
      const raw = text.trim()
      const compact = raw.toLowerCase().replace(/\s+/g, '')
      if (/^(kaishijisuan|kaishiyuyan|kaishimoni|queren|jixu|yes|ok|start|go)$/.test(compact)) return true
      return /^(确认|可以|好的|好|按默认|默认|开始|执行|继续|是|yes|ok)$/i.test(raw)
        || /(开始|执行|计算|模拟|预演|重算|确认)/.test(raw)
        || /按.*(计算|执行|模拟|预演)/.test(raw)
    },

    _isCancelWorkflow(text) {
      return /(取消|暂停|先不用|不用了|停止|退出任务)/.test(text)
    },

    _parseFloodSlots(text, existing = {}) {
      const slots = { ...existing }
      const frequency = text.match(/(\d{1,3})\s*年一遇/)
      if (frequency) slots.frequency = `${frequency[1]}年一遇`
      if (/百年|100年/.test(text)) slots.frequency = '100年一遇'
      if (/五十年|50年/.test(text)) slots.frequency = '50年一遇'
      if (/二十年|20年/.test(text)) slots.frequency = '20年一遇'
      if (/保守/.test(text)) slots.dispatchMode = '保守调度'
      if (/均衡/.test(text)) slots.dispatchMode = '均衡调度'
      if (/泄洪优先|抢泄|预泄/.test(text)) slots.dispatchMode = '泄洪优先'
      const targets = []
      if (/下游城镇/.test(text)) targets.push('下游城镇')
      if (/施工区/.test(text)) targets.push('施工区')
      if (/库区/.test(text)) targets.push('库区')
      if (targets.length) slots.target = targets.join('和')
      const peak = text.match(/(?:洪峰|峰值|流量)\s*(\d{3,5})/)
      if (peak) slots.peakFlow = Number(peak[1])
      return slots
    },

    _parseDispatchSlots(text, existing = {}) {
      const slots = { ...existing }
      if (/保守/.test(text) || /均衡/.test(text) || /泄洪优先|固定|补偿/.test(text)) {
        slots.schemes = []
        if (/保守/.test(text)) slots.schemes.push('保守调度')
        if (/均衡/.test(text)) slots.schemes.push('均衡调度')
        if (/泄洪优先/.test(text)) slots.schemes.push('泄洪优先')
        if (/固定/.test(text)) slots.schemes.push('固定下泄')
        if (/补偿/.test(text)) slots.schemes.push('补偿调度')
      }
      const level = text.match(/(?:水位|库水位)\s*(\d{3}(?:\.\d+)?)/)
      if (level) slots.level = Number(level[1])
      const peak = text.match(/(?:洪峰|入库|来水|流量)\s*(\d{3,5})/)
      if (peak) slots.peakFlow = Number(peak[1])
      if (/防洪/.test(text)) slots.objective = '防洪优先，控制最高库水位'
      if (/供水/.test(text)) slots.objective = '供水保障优先，兼顾生态基流'
      if (/发电/.test(text)) slots.objective = '发电效益优先，兼顾防洪约束'
      return slots
    },

    _parseSupplySlots(text, existing = {}) {
      const slots = { ...existing }
      const area = text.match(/(韩城|薛峰|龙门|张家卯|宜川|古贤|东庄)[^，,。；; ]*(供水区|灌区|片区|区域)?/)
      if (area) slots.area = area[0]
      const period = text.match(/未来\s*(\d+)\s*(小时|天|日)/)
      if (period) slots.period = `未来${period[1]}${period[2]}`
      if (/今天|今日/.test(text)) slots.period = '今日'
      return slots
    },

    _hasFloodRequiredSlots(slots) {
      return !!(slots.frequency && slots.dispatchMode && slots.target)
    },

    _hasDispatchRequiredSlots(slots) {
      return !!(slots.level && slots.peakFlow && slots.objective)
    },

    _buildFloodQuestion(slots) {
      const merged = { ...DEFAULT_WORKFLOW_SLOTS.flood, ...slots }
      if (this._hasFloodRequiredSlots(slots)) {
        return `已识别：${slots.frequency}、${slots.dispatchMode}、关注${slots.target}。我可以直接启动，也可以继续补充洪峰流量，例如“洪峰6200”。`
      }
      return `我建议按${merged.frequency}、${merged.dispatchMode}、关注${merged.target}先建立预演场景。继续说“开始预演”即可执行，也可以直接改成“50年一遇，泄洪优先，关注下游城镇”。`
    },

    _buildDispatchQuestion(slots) {
      const merged = { ...DEFAULT_WORKFLOW_SLOTS.dispatch, ...slots }
      const schemes = (slots.schemes && slots.schemes.length ? slots.schemes : merged.schemes).join('、')
      return `我准备对比${schemes}。默认工况为库水位 ${merged.level} m、预报洪峰 ${merged.peakFlow} m³/s、目标为${merged.objective}。继续说“开始计算”，或直接改成“水位616.8，洪峰5000，目标防洪”。`
    },

    async _executeFloodWorkflow(slots) {
      const merged = { ...DEFAULT_WORKFLOW_SLOTS.flood, ...slots }
      const frequencyConfig = getFloodFrequencyConfig(merged.frequency)
      if (!slots.peakFlow) merged.peakFlow = frequencyConfig.peakFlow
      const autoRunSchemeNum = this._schemeNumberFromDispatchMode(merged.dispatchMode)
      const scheme = scaleFloodSchemeForFrequency(this._schemeModeFromNumber(autoRunSchemeNum), merged.frequency)
      const huadong = await runHuadongModelChain({
        station: '古贤-龙门区间',
        horizon_hours: 24,
        peak_flow: merged.peakFlow,
        warning_threshold: 300,
        thresholds: { flood: 300, severe: 500 },
      })
      const scenario = await runFloodScenario({
        scenario_id: 'smart-orb-flood',
        scenario_name: `${merged.frequency}${merged.dispatchMode}洪水演进`,
        peak_flow: merged.peakFlow,
        dispatch_mode: merged.dispatchMode,
        target: merged.target,
        huadong_result: huadong,
        dispatch_result: this._schemeToFloodDispatch(scheme),
      })
      this._openBusinessPanel('floodSim', { workflow: merged, scenario, autoRunSchemeNum, huadong })
      this.recentWorkflow = { type: 'flood', slots: { ...merged } }
      const reply = this._buildFloodExpertBriefing({ scheme, scenario, slots: merged, autoRunSchemeNum, huadong })
      this._pushAssistant(reply)
    },

    async _executeFloodSchemeWorkflow(text) {
      const schemeNum = this._parseFloodSchemeNumber(text)
      const baseSlots = this.recentWorkflow && this.recentWorkflow.type === 'flood'
        ? this.recentWorkflow.slots
        : DEFAULT_WORKFLOW_SLOTS.flood
      const parsedSlots = this._parseFloodSlots(text, {})
      const slots = {
        ...baseSlots,
        ...parsedSlots,
        dispatchMode: this._schemeModeFromNumber(schemeNum).dispatchMode,
      }
      const frequencyConfig = getFloodFrequencyConfig(slots.frequency)
      if (!parsedSlots.peakFlow) slots.peakFlow = frequencyConfig.peakFlow
      const scheme = scaleFloodSchemeForFrequency(this._schemeModeFromNumber(schemeNum), slots.frequency)
      const huadong = await runHuadongModelChain({
        station: '古贤-龙门区间',
        horizon_hours: 24,
        peak_flow: slots.peakFlow,
        warning_threshold: 300,
        thresholds: { flood: 300, severe: 500 },
      })
      const scenario = await runFloodScenario({
        scenario_id: `smart-orb-${schemeNum}`,
        scenario_name: `${slots.frequency}${scheme.name}洪水演进`,
        peak_flow: slots.peakFlow,
        dispatch_mode: slots.dispatchMode,
        target: slots.target,
        huadong_result: huadong,
        dispatch_result: this._schemeToFloodDispatch(scheme),
      })
      this._openBusinessPanel('floodSim', {
        workflow: slots,
        scenario,
        autoRunSchemeNum: schemeNum,
        huadong,
      })
      this.recentWorkflow = { type: 'flood', slots: { ...slots } }
      this.pendingWorkflow = { type: 'flood', slots }
      this._pushAssistant(this._buildFloodExpertBriefing({ scheme, scenario, slots, autoRunSchemeNum: schemeNum, huadong }))
    },

    _schemeToFloodDispatch(scheme) {
      const timeline = scheme.timeline || {}
      return {
        times: timeline.time || [],
        curves: {
          inflow: timeline.inflow || [],
          release: timeline.release || [],
          level: timeline.level || [],
        },
        metrics: {
          peak_release: scheme.peakRelease,
          max_level: scheme.maxLevel,
          peak_reduction_rate: scheme.peakReduction,
          risk_level: scheme.risk,
        },
      }
    },

    _buildFloodExpertBriefing({ scheme, scenario, slots, autoRunSchemeNum, huadong }) {
      const timelineNarration = this._buildFloodTimelineNarration(scheme)
      const modelNarration = this._buildHuadongModelNarration(huadong)
      return `已按${slots.frequency}、${scheme.name}（${scheme.dispatchMode}）启动洪水演进，并自动进入方案${autoRunSchemeNum}预演。专家汇报如下：${scheme.name}属于${scheme.focus}，综合判定为${scheme.risk}；调度思路：${scheme.expertComment} 关键指标为：水库峰值下泄 ${scheme.peakRelease} m³/s，最高库水位 ${scheme.maxLevel} m，削峰率 ${scheme.peakReduction}%，预计最大淹没面积 ${scheme.inundationArea} km²，到峰时间约为${scheme.peakTime}。${timelineNarration} ${modelNarration} 本方案防护侧重为：${scheme.controlTarget}；适用条件为：${scheme.condition}。龙门断面路由结果显示：断面洪峰约 ${scenario.metrics.peak_flow} m³/s，最高演进水位 ${scenario.metrics.max_stage} m，风险等级 ${scenario.metrics.risk_level}。建议现场继续关注龙门断面水位涨率、下游城镇预警响应、施工区撤离通道和退水阶段道路恢复。`
    },

    _buildHuadongModelNarration(huadong) {
      if (!huadong || !huadong.metrics) return ''
      const m = huadong.metrics
      return `华东水文模型链已完成联算：${m.forecast_summary || '新安江、GR4J、RF、LSTM四模型预报完成'}；${m.analysis_summary || '趋势、周期和突变分析完成'}；${m.ensemble_summary || '多模型集合预报完成'}；${m.correction_summary || '预报误差订正完成'}；${m.risk_summary || '超阈概率和分位风险分析完成'}；${m.warning_summary || '洪水干旱预警完成'}。综合预警等级为${m.warning_level_cn || '无明显预警'}。`
    },

    _buildFloodTimelineNarration(scheme) {
      const line = scheme.timeline
      if (!line) return ''
      const peakIndex = line.release.reduce((best, value, index) => value > line.release[best] ? index : best, 0)
      const earlyIndex = Math.min(3, line.time.length - 1)
      const lateIndex = line.time.length - 1
      const early = {
        time: line.time[earlyIndex],
        inflow: line.inflow[earlyIndex],
        release: line.release[earlyIndex],
        level: line.level[earlyIndex],
        area: line.area[earlyIndex],
      }
      const peak = {
        time: line.time[peakIndex],
        inflow: line.inflow[peakIndex],
        release: line.release[peakIndex],
        level: line.level[peakIndex],
        area: line.area[peakIndex],
      }
      const late = {
        time: line.time[lateIndex],
        inflow: line.inflow[lateIndex],
        release: line.release[lateIndex],
        level: line.level[lateIndex],
        area: line.area[lateIndex],
      }
      return `24小时洪水演进过程线显示：0到${early.time}为快速上涨段，入库流量升至 ${early.inflow} m³/s，下泄控制在 ${early.release} m³/s，库水位约 ${early.level} m，淹没面积约 ${early.area} km²；${peak.time}附近进入峰现控制段，下泄达到 ${peak.release} m³/s，库水位约 ${peak.level} m，淹没面积约 ${peak.area} km²；至${late.time}进入退水复盘段，入库回落至 ${late.inflow} m³/s，下泄 ${late.release} m³/s，库水位回落至 ${late.level} m，淹没面积收缩至 ${late.area} km²。`
    },

    async _executeDispatchWorkflow(slots) {
      const merged = { ...DEFAULT_WORKFLOW_SLOTS.dispatch, ...slots }
      const result = await compareDispatch({
        initial_level: merged.level,
        peak_flow: merged.peakFlow,
        objective: merged.objective,
        schemes: merged.schemes,
      })
      this._openBusinessPanel('custom', { workflow: merged, dispatchResult: result })
      this.recentWorkflow = { type: 'dispatch', slots: { ...merged } }
      const best = result.recommended || result.schemes[0]
      const reply = `调度方案比选已完成并打开推演面板。推荐采用${best.scheme_name}：最高库水位 ${best.metrics.max_level} m，削峰率 ${best.metrics.peak_reduction_ratio}% ，最大下泄 ${best.metrics.max_release} m³/s，综合风险 ${best.metrics.risk_score}。继续说“按推荐方案做洪水演进”即可联动下一步。`
      this.pendingWorkflow = { type: 'flood', slots: { dispatchMode: best.scheme_name, frequency: '20年一遇', target: '下游城镇和施工区', peakFlow: merged.peakFlow } }
      this._pushAssistant(reply)
    },

    async _executeProjectBrief() {
      const [level, inflow, rain, sediment, supply] = await Promise.all([
        resolveBusinessDataQuestion('古贤水库当前水位'),
        resolveBusinessDataQuestion('古贤水库当前入库流量'),
        resolveBusinessDataQuestion('古贤水库当前雨量'),
        resolveBusinessDataQuestion('古贤水库当前含沙量'),
        resolveBusinessDataQuestion('供需缺口当前情况'),
      ])
      const monitoringParts = [level, inflow, rain, sediment]
        .filter(Boolean)
        .map(item => `${item.snapshot.metric.label} ${item.snapshot.current}${item.snapshot.metric.unit ? ` ${item.snapshot.metric.unit}` : ''}`)
      const sources = Array.from(new Set([level, inflow, rain, sediment, supply]
        .filter(Boolean)
        .map(item => item.source === 'frontend_fallback' ? '前端兜底数据' : (item.type === 'supplyDemand' ? '统一供需 API' : '统一水情 API'))))
      const supplySummary = supply && supply.snapshot && supply.snapshot.summary
        ? `供需缺口 ${supply.snapshot.summary.total_deficit_10k_m3} 万m3，预警区域 ${supply.snapshot.summary.warning_region_count} 个`
        : '供需汇总暂不可用'
      const reply = `当前工程运行态势已按统一接口汇总：${monitoringParts.join('，') || '水情指标暂不可用'}；${supplySummary}。来源：${sources.join('、') || '未知'}。已打开综合监控大屏。你想继续看哪一类：水情风险、调度建议、施工安全，还是供需缺口？`
      this._openBusinessPanel('monitoring', {
        briefing: {
          monitoring: [level, inflow, rain, sediment].filter(Boolean).map(item => item.snapshot),
          supply: supply ? supply.snapshot : null,
          sources,
        },
      })
      this.pendingWorkflow = { type: 'briefing', slots: {} }
      this._pushAssistant(reply)
    },

    async _executeSupplyWorkflow(slots = {}) {
      const answer = await resolveBusinessDataQuestion('供需缺口当前情况')
      const snapshot = answer && answer.snapshot
      if (!snapshot) {
        this._openBusinessPanel('supplyDemand', { workflow: slots })
        this._pushAssistant('供需平衡分析面板已打开，但当前统一供需 API 暂未返回汇总数据，请检查数据源连接。')
        return
      }
      const area = slots.area || DEFAULT_WORKFLOW_SLOTS.supply.area
      const period = slots.period || DEFAULT_WORKFLOW_SLOTS.supply.period
      const summary = snapshot.summary || {}
      const sourceText = answer.source === 'frontend_fallback' ? '前端兜底数据' : '统一供需 API'
      this._openBusinessPanel('supplyDemand', { query: snapshot, workflow: slots, source: answer.source })
      this._pushAssistant(`${area}${period}供需平衡已按当前数据生成：实际供水 ${summary.total_actual_supply_10k_m3} 万m3，总需水 ${summary.total_demand_10k_m3} 万m3，总缺口 ${summary.total_deficit_10k_m3} 万m3，预警区域 ${summary.warning_region_count} 个。来源：${sourceText}。已打开供需平衡分析面板。`)
    },

    async _executeBriefingFollowUp(text) {
      if (/调度/.test(text)) {
        this.pendingWorkflow = { type: 'dispatch', slots: {} }
        this._pushAssistant(this._buildDispatchQuestion({}))
        return
      }
      if (/供需|缺口|供水/.test(text)) {
        await this._executeSupplyWorkflow({})
        return
      }
      if (/施工|安全/.test(text)) {
        this._openBusinessPanel('construction')
        this._pushAssistant('施工安全态势已切换：当前重点关注高边坡作业、拌合楼设备状态和导流洞巡检，建议维持黄色关注级别并加强夜间巡检。')
        return
      }
      this._openBusinessPanel('monitoring')
      this._pushAssistant('水情风险已展开：当前雨情未触发红色预警，但上游来水有抬升趋势，建议继续跟踪未来6小时入库洪峰和张家卯断面浊度变化。')
    },

    async _continueRecentWorkflow() {
      if (!this.recentWorkflow) return false
      if (this.recentWorkflow.type === 'flood') {
        this._pushAssistant('收到，沿用上一轮洪水演进工况重新计算，并刷新预演面板。')
        await this._executeFloodWorkflow(this.recentWorkflow.slots)
        return true
      }
      if (this.recentWorkflow.type === 'dispatch') {
        this._pushAssistant('收到，沿用上一轮调度工况重新计算，并刷新方案比选结果。')
        await this._executeDispatchWorkflow(this.recentWorkflow.slots)
        return true
      }
      return false
    },

    _pushAssistant(text) {
      this.chatHistory.push({ role: 'ai', text })
      this.$nextTick(() => this._scrollChat())
      this._speakAndAnimate(text)
    },

    async _tryMonitoringQuestion(text) {
      const answer = await resolveBusinessDataQuestion(text)
      if (!answer) return false

      const snapshot = answer.snapshot
      const reply = answer.reply
      this.chatHistory.push({ role: 'ai', text: reply })

      // 飞行到站点/水库位置
      if (snapshot.station && snapshot.station.location) {
        const loc = snapshot.station.location
        const viewer = window.viewer
        if (viewer) {
          const alt = 8000
          const pitch = -0.8727
          const heading = 0.5
          const offset = alt / Math.tan(Math.abs(pitch))
          const camLat = loc[1] - (offset * Math.cos(heading)) / 111320
          const camLng = loc[0] - (offset * Math.sin(heading)) / (111320 * Math.cos(loc[1] * Math.PI / 180))
          viewer.camera.flyTo({
            destination: Cesium.Cartesian3.fromDegrees(camLng, camLat, alt),
            orientation: { heading, pitch, roll: 0 },
            duration: 1.5,
          })
        }
      }

      if (answer.type === 'supplyDemand') {
        this._openBusinessPanel('supplyDemand', { query: snapshot, source: answer.source })
      } else {
        // 如果是水库站点，同时打开水库信息面板
        const stationName = snapshot.station && snapshot.station.name
        const reservoir = stationName ? findReservoirByName(stationName) : null
        if (reservoir && reservoir.id !== 'guxian' && snapshot.station.reservoir) {
          // 非古贤水库：打开 regulatingReservoir 面板（含完整工程参数）
          this._openBusinessPanel('regulatingReservoir', {
            info: {
              name: reservoir.name,
              location: reservoir.location,
              source: reservoir.coordinateNote,
              reservoir: reservoir,
              snapshot: reservoir.snapshot,
            }
          })
        } else {
          this._openBusinessPanel('monitoring', {
            title: `${snapshot.station.name}${snapshot.metric.label}监测`,
            query: snapshot,
            source: answer.source,
          })
        }
      }
      this.$nextTick(() => this._scrollChat())
      this._speakAndAnimate(reply)
      return true
    },

    _tryReservoirParameterQuestion(text) {
      // 如果包含"来水预测方案"等预测关键词，不走水库信息逻辑，交给 _tryKeywordMatch 处理
      if (/(来水预测方案|预测方案|多方案预测|多方案对比|方案对比|预报对比|精度评定)/.test(text)) return false
      // 匹配两种情况：
      // 1. 包含参数关键词（参数|属性|库容|...）→ 显示工程参数
      // 2. 直接提到已知水库名称（薛峰、东庄、龙潭...）→ 打开水库面板并播报简讯
      const hasParamKeyword = /(参数|属性|库容|正常蓄水位|死水位|汛限|设计洪水位|校核洪水位|装机|工程指标|水库信息)/.test(text)
      const reservoir = findReservoirByName(text)
      // findReservoirByName 默认返回古贤，需要确认确实匹配到了非默认水库
      // 或文本中明确包含"古贤"
      const isExplicitReservoir = reservoir && (
        reservoir.aliases.some(a => text.includes(a)) ||
        text.includes(reservoir.name)
      )
      if (!hasParamKeyword && !isExplicitReservoir) return false

      // 飞行到水库位置
      this._flyToReservoir(reservoir)

      // 构建含监测数据的综合简讯
      const reply = this._buildReservoirParameterBrief(reservoir)
      this.chatHistory.push({ role: 'ai', text: reply })
      // 打开水库信息面板（含完整工程参数+监测快照）
      const panelKey = reservoir.id === 'guxian' ? 'reservoirInfo' : 'regulatingReservoir'
      this._openBusinessPanel(panelKey, {
        info: {
          name: reservoir.name,
          location: reservoir.location,
          source: reservoir.coordinateNote,
          reservoir: reservoir,
          snapshot: reservoir.snapshot,
        }
      })
      this.$nextTick(() => this._scrollChat())
      this._speakAndAnimate(reply)
      return true
    },

    /**
     * 飞行到水库/设施位置（使用三角函数计算偏移以实现倾斜视角对焦）
     */
    _flyToReservoir(reservoir) {
      if (!reservoir || !reservoir.location) return
      const viewer = window.viewer
      if (!viewer) return
      const lng = reservoir.location[0]
      const lat = reservoir.location[1]
      // 倾斜视角：高度 8000m，俯角 -50°，航向 0.5 弧度
      const alt = 8000
      const pitch = -0.8727
      const heading = 0.5
      const offset = alt / Math.tan(Math.abs(pitch))
      const camLat = lat - (offset * Math.cos(heading)) / 111320
      const camLng = lng - (offset * Math.sin(heading)) / (111320 * Math.cos(lat * Math.PI / 180))
      viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(camLng, camLat, alt),
        orientation: { heading, pitch, roll: 0 },
        duration: 1.5,
      })
    },

    /**
     * 飞回3D地球初始俯瞰视角（古贤水利枢纽全貌）
     */
    _flyToHomeView() {
      const viewer = window.viewer
      if (!viewer) return
      viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(109.461393, 35.828187, 599097.0271643074),
        orientation: {
          heading: 6.2750128595724615,
          pitch: -1.570681686183447,
          roll: 0
        },
        duration: 1.5,
      })
    },

    /**
     * 清理3D地球上所有业务叠加图层（洪水实体、应急图层、动态范围等）
     */
    _cleanupAllGisOverlays() {
      // 通过 App.vue 的 $refs 获取 CesiumScene 组件
      // SmartOrb 是 App.vue 的子组件，通过 $parent 访问
      const app = this.$parent
      const cesiumScene = app && app.$refs ? app.$refs.cesiumScene : null
      if (cesiumScene) {
        // 清理洪水淹没分析
        if (cesiumScene.adjustVisible && cesiumScene.$refs.child && cesiumScene.$refs.child.clear) {
          cesiumScene.$refs.child.clear(false)
          cesiumScene.adjustVisible = false
        }
        // 清理应急 GIS 图层
        if (cesiumScene.clearEmergencyGisLayer) {
          cesiumScene.clearEmergencyGisLayer()
        }
        // 清理洪水3D动画
        if (cesiumScene.stopFloodInundationAnimation) {
          cesiumScene.stopFloodInundationAnimation()
        }
      }
    },

    _buildReservoirParameterBrief(reservoir) {
      const levels = reservoir.characteristic_levels || {}
      const capacities = reservoir.capacities || {}
      const engineering = reservoir.engineering || {}
      const floodStandard = reservoir.flood_standard || {}
      const snap = reservoir.snapshot || {}
      const capacityText = [
        capacities.total_capacity !== undefined ? `总库容 ${capacities.total_capacity} 亿m³` : '',
        capacities.regulation_capacity !== undefined ? `调节库容 ${capacities.regulation_capacity} 亿m³` : '',
      ].filter(Boolean).join('，')
      const levelText = [
        levels.normal_level !== undefined ? `正常蓄水位 ${levels.normal_level} m` : '',
        levels.dead_level !== undefined ? `死水位 ${levels.dead_level} m` : '',
        levels.flood_limit_level !== undefined ? `汛限水位 ${levels.flood_limit_level} m` : '',
      ].filter(Boolean).join('，')
      const monitoringText = snap.level != null
        ? `当前监测：库水位 ${snap.level} m，入库流量 ${snap.inflow ?? '-'} m3/s，出库流量 ${snap.outflow ?? '-'} m3/s。`
        : ''
      const extra = [
        reservoir.reservoir_type ? `水库类型 ${reservoir.reservoir_type}` : '',
        reservoir.river ? `所在河流 ${reservoir.river}` : '',
        reservoir.catchment_area_km2 !== undefined ? `集雨面积 ${reservoir.catchment_area_km2} km²` : '',
        engineering.built_year !== undefined ? `建成年份 ${engineering.built_year} 年` : '',
      ].filter(Boolean).join('，')
      return `${reservoir.name}：${capacityText}。${levelText}。${monitoringText}${extra ? extra + '。' : ''}`
    },

    _normalizeInputText(text) {
      return String(text || '')
        .replace(/^\/plan\s*/i, '')
        .replace(/^\/\w+\s*/i, '')
        .trim()
    },

    _isMainScreenIntent(text) {
      // 支持单独说"返回"、"关闭"、"回去"等简短指令
      return /(返回|回到|退出|关闭|回去|回退).*(主界面|首页|主页|大屏|上一步|上一页)|^(返回|返回主界面|返回首页|回首页|回主页|关闭|关闭窗口|关闭面板|退出|退出系统|回去|上一页|上一步)$/.test(text)
    },

    _startsNewBusinessTask(text) {
      if (this._isFloodSchemeIntent(text)) return true
      if (this._isConfirmation(text) || this._isCancelWorkflow(text)) return false
      return this._isFloodIntent(text)
        || this._isStorylineIntent(text)
        || this._isEmergencyPlanIntent(text)
        || this._isSupplyDispatchIntent(text)
        || this._isDispatchIntent(text)
        || this._isProjectBriefIntent(text)
        || this._isSupplyIntent(text)
    },

    _shouldAutoRunFlood(text, slots) {
      // "洪水演进"/"洪水预演"/"洪水模拟"直接自动开始
      if (/洪水演进|洪水预演|洪水模拟|洪演/.test(text)) return true
      return /(模拟|预演|推演|开始|启动|进行|做|计算|现在|当前|按默认|确认|重算)/.test(text) || this._hasFloodRequiredSlots(slots)
    },

    _shouldAutoRunDispatch(text, slots) {
      return /(对比|比选|计算|预演|开始|启动|进行|按默认|确认)/.test(text) || this._hasDispatchRequiredSlots(slots)
    },

    _formatLLMError(error) {
      const message = error && error.message ? error.message : String(error || '')
      if (message.includes('VUE_APP_API_GATEWAY_TOKEN') || message.includes('VUE_APP_API_GATEWAY_URL')) {
        return '业务 API 网关尚未配置，请配置网关地址和最小权限访问 Token。'
      }
      if (message.startsWith('API ')) {
        return `大模型接口返回错误：${message}`
      }
      return '大模型接口暂时无法连接，请检查 API 地址、Key、代理或网络。'
    },

    _getConversationContext() {
      if (!this._llmContext) this._llmContext = []
      return this._llmContext
    },

    _speakAndAnimate(text) {
      if (!text) return
      if (this.voiceService) {
        // 屏幕上仍显示原始文本，但语音输出经过 TTS 规范化：
        // 单位符号转中文（m³→立方米、m3/s→立方米每秒等）、精简冗长同步指标列表
        const ttsText = normalizeForTTS(text)
        this.voiceService.speak(ttsText)
      }
      this._pollSpeakingState()
    },

    _pollSpeakingState() {
      if (this.orbState === 'thinking' && this.isThinking) return
      if (this.voiceService && this.voiceService.isSpeaking) {
        this.orbState = 'answering'
      }
      const check = setInterval(() => {
        if (this.voiceService && this.voiceService.isSpeaking) {
          this.orbState = 'answering'
        }
        if (!this.voiceService || !this.voiceService.isSpeaking) {
          clearInterval(check)
          if (!this.isThinking) this._returnToConnectedIdle()
        }
      }, 200)
    },

    togglePanel() {
      if (this.dragMoved) return
      this.suppressIdleBubble = false
      this.panelOpen = !this.panelOpen
      if (!this.panelOpen) {
        this._cancelListeningStart()
        if (!this.isThinking) this._returnToConnectedIdle()
      }
    },
    closeChatPanel() {
      this.panelOpen = false
      this._cancelListeningStart()
      if (!this.isThinking && !this.isKnowledgeOrbFlying) this._returnToConnectedIdle()
    },
    _cancelListeningStart() {
      const shouldStop = this.isListening || this.isListeningStarting
      // 使尚未完成的 startListening Promise 失效，防止面板关闭后麦克风反而启动。
      this._listeningRequestId += 1
      this.isListeningStarting = false
      if (shouldStop && this.voiceService) this.voiceService.stopListening()
      this.isListening = false
    },
    async toggleListening() {
      if (!this.voiceService) return
      if (this.isListeningStarting) {
        this._listeningRequestId += 1
        this.isListeningStarting = false
        this.voiceService.stopListening()
        this._returnToConnectedIdle()
        return
      }
      if (this.isListening) {
        this._listeningRequestId += 1
        this.voiceService.stopListening()
        this.isListening = false
        this._returnToConnectedIdle()
        return
      }

      const terminalStatus = this.terminalSession && this.terminalSession.getStatus()
      if (!terminalStatus || terminalStatus.control !== 'connected'
        || !terminalStatus.freshness.control.fresh) {
        this.orbState = 'offline'
        this.chatHistory.push({ role: 'ai', text: 'Rock5A 终端尚未连接，暂时无法启用语音。' })
        this.$nextTick(() => this._scrollChat())
        return
      }
      if (this.isThinking || ['thinking', 'answering', 'rendering'].includes(this.orbState)) return

      const requestId = ++this._listeningRequestId
      this.isListeningStarting = true
      const result = await this.voiceService.startListening()
      if (requestId !== this._listeningRequestId) return
      this.isListeningStarting = false

      if (result && result.ok) {
        this.isListening = true
        if (this._stateMachine && this._stateMachine.isIdle()) {
          this._stateMachine.startListening('rock5a-terminal')
        }
        this.orbState = 'listening'
        return
      }

      this.isListening = false
      this._returnToConnectedIdle()
      const reasonText = {
        stt_credentials_missing: '语音识别服务尚未配置，请检查前端环境变量。',
        audio_unavailable: 'Rock5A 麦克风通道未就绪，请检查 ReSpeaker。',
        rock5a_not_connected: 'Rock5A 终端连接已断开。',
        stt_socket_create_failed: '语音识别连接创建失败，请稍后重试。',
        stt_connect_timeout: '语音识别连接超时，请检查网络。',
      }[(result && result.reason) || ''] || '语音链路启动失败，请稍后重试。'
      this.chatHistory.push({ role: 'ai', text: reasonText })
      this.$nextTick(() => this._scrollChat())
    },
    async handleTextSubmit() {
      const rawText = this.textInput.trim()
      const text = this._normalizeInputText(rawText)
      if (!text) return
      this.textInput = ''
      this.chatHistory.push({ role: 'user', text })
      this.orbState = 'thinking'
      this._processInput(text, multimodalContext.resolveVoice(text))
    },
    _scrollChat() {
      if (this.$refs.chatBody) {
        this.$refs.chatBody.scrollTop = this.$refs.chatBody.scrollHeight
      }
    }
  },
  beforeDestroy() {
    this._listeningRequestId += 1
    if (this.voiceService) this.voiceService.stopListening()
    if (this._terminalUnsubscribe) this._terminalUnsubscribe()
    if (this._stateUnsubscribe) this._stateUnsubscribe()
    if (this._multimodalUnsubscribe) this._multimodalUnsubscribe()
    if (this._gestureActionHandler && this.gestureService) {
      this.gestureService.offAction(this._gestureActionHandler)
    }
    if (this._bubbleTimer) clearInterval(this._bubbleTimer)
    window.clearTimeout(this._openPanelTimer)
    window.clearTimeout(this._finishPanelFlightTimer)
    window.removeEventListener('mousemove', this.onDragMove)
    window.removeEventListener('mouseup', this.endDrag)
    window.removeEventListener('touchmove', this.onDragMove)
    window.removeEventListener('touchend', this.endDrag)
    window.removeEventListener('resize', this.onViewportResize)
  }
}
</script>

<style>
@property --angle {
  syntax: "<angle>";
  inherits: false;
  initial-value: 0deg;
}
</style>

<style scoped>
.smart-orb-root {
  position: fixed;
  right: 32px;
  bottom: 26px;
  width: 112px;
  height: 124px;
  z-index: 10030;
  display: block;
  pointer-events: none;
  transition: right 0.35s ease, bottom 0.35s ease, left 0.25s ease, top 0.25s ease, transform 0.25s ease;
}

.smart-orb-root.has-business-panel {
  right: 18px;
  bottom: 18px;
}

.smart-orb-root.is-compact .siri-orb {
  transform: scale(0.72);
  transform-origin: center;
}

.smart-orb-root.is-compact .orb-glow {
  opacity: 0.65;
}

.smart-orb-root.is-knowledge-launching .orb-wrapper,
.smart-orb-root.is-knowledge-docked .orb-wrapper {
  opacity: 0;
  transform: scale(0.45) translateY(16px);
  pointer-events: none;
}

.smart-orb-root.is-dragging {
  transition: none;
  cursor: grabbing;
}

.smart-orb-root.chat-open.has-business-panel:not(.is-dragging) {
  right: 18px;
  left: auto;
  bottom: 18px;
}

.smart-orb-root.chat-open.has-business-panel:not(.is-dragging) .dialog-panel {
  transform-origin: bottom right;
  right: auto;
}

.smart-orb-root.chat-open.has-business-panel:not(.is-dragging) .idle-prompt-bubble {
  border-radius: 18px 18px 18px 6px;
}

.dialog-panel {
  position: absolute;
  right: 0;
  bottom: calc(100% + 14px);
  width: 360px;
  max-height: 440px;
  background: rgba(10, 10, 16, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 16px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05);
  pointer-events: auto;
}

.knowledge-portal-overlay {
  position: fixed;
  inset: 0;
  z-index: 10018;
  pointer-events: none;
  display: grid;
  place-items: center;
}

.knowledge-portal-ring,
.knowledge-portal-core {
  grid-area: 1 / 1;
  border-radius: 50%;
}

.knowledge-portal-ring {
  width: 120px;
  height: 120px;
  background:
    conic-gradient(from var(--angle),
      #ff4ecd,
      #7c3cff,
      #25d9ff,
      #40ffba,
      #f8ff59,
      #ff8a3d,
      #ff4ecd);
  filter: blur(2px) saturate(1.4);
  opacity: 0;
  animation: knowledge-portal-expand 0.9s cubic-bezier(.16, 1, .3, 1) forwards,
             orb-rotate 1.2s linear infinite;
  mask-image: radial-gradient(circle, transparent 46%, black 49%, black 58%, transparent 62%);
  -webkit-mask-image: radial-gradient(circle, transparent 46%, black 49%, black 58%, transparent 62%);
}

.knowledge-portal-core {
  width: 72px;
  height: 72px;
  background:
    radial-gradient(circle, rgba(255,255,255,0.8), rgba(37,217,255,0.22) 38%, transparent 70%);
  filter: blur(10px);
  animation: knowledge-portal-core 0.75s ease-out forwards;
}

.knowledge-portal-enter-active,
.knowledge-portal-leave-active {
  transition: opacity 0.2s ease;
}

.knowledge-portal-enter,
.knowledge-portal-leave-to {
  opacity: 0;
}

.knowledge-orb-flight {
  position: fixed;
  left: 0;
  top: 0;
  width: var(--from-size);
  height: var(--from-size);
  z-index: 10035;
  pointer-events: none;
  border-radius: 50%;
  transform: translate3d(var(--from-x), var(--from-y), 0) scale(1);
  transform-origin: center;
  will-change: transform, opacity, filter;
  background:
    conic-gradient(from var(--angle),
      #ff4ecd,
      #7c3cff,
      #25d9ff,
      #40ffba,
      #f8ff59,
      #ff8a3d,
      #ff4ecd);
  box-shadow:
    0 0 38px rgba(124, 60, 255, 0.36),
    0 0 48px rgba(37, 217, 255, 0.26),
    inset rgba(10, 10, 16, 0.66) 0 0 18px 4px;
  filter: blur(0.2px) saturate(1.45);
  animation:
    knowledge-orb-open var(--flight-duration, 1.12s) cubic-bezier(.16, 1, .3, 1) forwards,
    orb-rotate 1.2s linear infinite;
}

.knowledge-orb-flight::after {
  content: "";
  position: absolute;
  inset: 11%;
  border-radius: 50%;
  background:
    radial-gradient(circle at 35% 28%, rgba(255,255,255,0.72), transparent 16%),
    radial-gradient(circle at center, rgba(8, 10, 22, 0.2), rgba(8, 10, 22, 0.62));
  backdrop-filter: blur(14px) contrast(1.8);
}

.knowledge-orb-flight::before {
  content: "";
  position: absolute;
  inset: -18px;
  border-radius: 50%;
  background:
    radial-gradient(circle, var(--trail-a, rgba(255, 78, 205, 0.34)), transparent 58%),
    radial-gradient(circle at 70% 30%, var(--trail-b, rgba(37, 217, 255, 0.28)), transparent 62%);
  filter: blur(14px);
  opacity: 0.72;
}

.knowledge-orb-flight.flight-flood {
  box-shadow:
    0 0 34px rgba(37, 217, 255, 0.42),
    0 18px 58px rgba(37, 121, 255, 0.28),
    inset rgba(8, 12, 28, 0.66) 0 0 18px 4px;
}

.knowledge-orb-flight.flight-flood::before {
  inset: -12px -42px -12px -16px;
  border-radius: 999px;
  transform: rotate(-12deg);
  opacity: 0.82;
}

.knowledge-orb-flight.flight-sheet::before {
  inset: -16px -22px -24px;
  border-radius: 46% 46% 58% 58%;
  opacity: 0.78;
}

.knowledge-orb-flight.flight-alert {
  animation:
    knowledge-orb-open var(--flight-duration, 0.96s) cubic-bezier(.2, .92, .12, 1) forwards,
    knowledge-alert-pulse 0.34s ease-in-out infinite alternate,
    orb-rotate 0.9s linear infinite;
}

.knowledge-orb-flight.flight-alert::before {
  inset: -28px;
  opacity: 0.9;
}

.knowledge-orb-flight.flight-storyline::before {
  inset: -28px -34px;
  opacity: 0.86;
}

.knowledge-orb-flight.flight-dock::before {
  inset: -14px -34px -14px -18px;
  border-radius: 999px;
  opacity: 0.76;
}

@keyframes knowledge-orb-open {
  0% {
    opacity: 1;
    transform: translate3d(var(--from-x), var(--from-y), 0) rotate(0deg) scale(1);
    filter: blur(0) saturate(1.45);
  }
  30% {
    opacity: 1;
    transform: translate3d(var(--mid-x), var(--mid-y), 0) rotate(var(--rotate-mid, -8deg)) scale(var(--mid-scale, 0.86));
    filter: blur(0) saturate(1.7);
  }
  64% {
    opacity: 1;
    transform: translate3d(var(--mid2-x), var(--mid2-y), 0) rotate(var(--rotate-settle, 5deg)) scale(calc(var(--to-scale) * 0.96));
    filter: blur(0) saturate(1.62);
  }
  80% {
    opacity: 1;
    transform: translate3d(var(--to-x), var(--to-y), 0) rotate(0deg) scale(var(--to-scale));
    filter: blur(0) saturate(1.6);
  }
  92% {
    opacity: 0.9;
    transform: translate3d(var(--to-x), var(--to-y), 0) rotate(0deg) scale(calc(var(--to-scale) * var(--settle-scale, 1.28)));
    filter: blur(2px) saturate(1.95);
  }
  100% {
    opacity: 0;
    transform: translate3d(var(--to-x), var(--to-y), 0) rotate(0deg) scale(calc(var(--to-scale) * var(--end-scale, 0.72)));
    filter: blur(8px) saturate(2.1);
  }
}

@keyframes knowledge-alert-pulse {
  from {
    box-shadow:
      0 0 30px rgba(255, 138, 61, 0.38),
      0 0 42px rgba(255, 78, 205, 0.22),
      inset rgba(10, 10, 16, 0.66) 0 0 18px 4px;
  }
  to {
    box-shadow:
      0 0 48px rgba(255, 138, 61, 0.58),
      0 0 72px rgba(255, 78, 205, 0.34),
      inset rgba(10, 10, 16, 0.66) 0 0 18px 4px;
  }
}

@keyframes knowledge-portal-expand {
  0% {
    opacity: 0;
    transform: scale(0.25);
  }
  18% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: scale(11);
  }
}

@keyframes knowledge-portal-core {
  0% {
    opacity: 0;
    transform: scale(0.35);
  }
  35% {
    opacity: 0.85;
  }
  100% {
    opacity: 0;
    transform: scale(6);
  }
}

.idle-prompt-bubble {
  position: absolute;
  right: 0;
  bottom: calc(100% + 12px);
  max-width: 280px;
  padding: 12px 16px;
  border: 1px solid rgba(110, 210, 255, 0.28);
  border-radius: 18px 18px 6px 18px;
  background: linear-gradient(135deg, rgba(8, 18, 34, 0.92), rgba(18, 52, 72, 0.86));
  color: rgba(255, 255, 255, 0.92);
  box-shadow: 0 10px 34px rgba(0, 0, 0, 0.34), 0 0 22px rgba(74, 158, 255, 0.14);
  cursor: pointer;
  font-size: 13px;
  line-height: 1.45;
  text-align: left;
  pointer-events: auto;
}
.idle-prompt-bubble::after {
  content: "";
  position: absolute;
}
.idle-bubble-enter-active,
.idle-bubble-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.idle-bubble-enter,
.idle-bubble-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.96);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.panel-close {
  background: rgba(255,255,255,0.06);
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 18px;
  cursor: pointer;
  width: 28px; height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}
.panel-close:hover { background: rgba(255,255,255,0.12); color: #fff; }

.multimodal-context-strip {
  padding: 8px 16px;
  border-bottom: 1px solid rgba(101, 246, 207, 0.18);
  background: rgba(12, 66, 73, 0.2);
  color: rgba(225, 255, 252, 0.82);
  font-size: 10px;
  line-height: 1.45;
}
.multimodal-context-strip div { display: flex; gap: 8px; align-items: baseline; }
.multimodal-context-strip span { min-width: 28px; color: rgba(153, 237, 226, 0.58); }
.multimodal-context-strip strong { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; }

.panel-body {
  flex: 1;
  padding: 14px 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 200px;
  max-height: 300px;
}
.panel-body::-webkit-scrollbar { width: 3px; }
.panel-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }

.chat-msg { display: flex; animation: msg-in 0.3s ease-out; }
@keyframes msg-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
.msg-user { justify-content: flex-end; }
.msg-ai { justify-content: flex-start; }

.msg-bubble {
  max-width: 82%;
  padding: 10px 15px;
  border-radius: 16px;
  font-size: 13px;
  line-height: 1.55;
  word-break: break-word;
  letter-spacing: 0.01em;
}
.msg-user .msg-bubble {
  background: linear-gradient(135deg, rgba(74,158,255,0.3), rgba(100,150,255,0.2));
  color: #fff;
  border-bottom-right-radius: 6px;
}
.msg-ai .msg-bubble {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
  border-bottom-left-radius: 6px;
}

.typing-indicator {
  display: flex; gap: 4px; padding: 10px 15px;
}
.typing-indicator span {
  width: 6px; height: 6px; border-radius: 50%;
  background: rgba(255,255,255,0.4);
  animation: typing-bounce 1.4s ease-in-out infinite;
}
.typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.4s; }
@keyframes typing-bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-6px); }
}

.quick-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding: 10px 14px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.quick-actions button {
  height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid rgba(74, 158, 255, 0.24);
  background: rgba(74, 158, 255, 0.10);
  color: rgba(214, 241, 255, 0.88);
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.2s ease;
}

.quick-actions button:hover {
  background: rgba(74, 158, 255, 0.20);
  border-color: rgba(74, 158, 255, 0.42);
  color: #fff;
}

.panel-input {
  display: flex;
  padding: 10px 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  gap: 8px;
  align-items: center;
}
.text-input {
  flex: 1;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 22px;
  padding: 9px 16px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  outline: none;
  transition: border-color 0.2s;
}
.text-input:focus { border-color: rgba(74, 158, 255, 0.4); }
.text-input::placeholder { color: rgba(255,255,255,0.25); }

.send-btn, .mic-btn {
  width: 38px; height: 38px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
}
.mic-btn.active { background: rgba(0, 224, 198, 0.15); border-color: rgba(0, 224, 198, 0.4); color: #00e0c6; }
.mic-btn.pending { color: #67e8f9; animation: hint-pulse 1s ease-in-out infinite; }
.send-btn:hover, .mic-btn:hover { background: rgba(255, 255, 255, 0.08); color: #fff; }

.panel-slide-enter-active { transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
.panel-slide-leave-active { transition: all 0.2s ease-in; }
.panel-slide-enter, .panel-slide-leave-to { opacity: 0; transform: translateY(24px) scale(0.92); }

.orb-wrapper {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 112px;
  min-height: 112px;
  display: flex; flex-direction: column; align-items: center;
  cursor: grab; user-select: none;
  pointer-events: auto;
  touch-action: none;
  transition: opacity 0.28s ease, transform 0.38s cubic-bezier(.16, 1, .3, 1);
}

.orb-wrapper:active { cursor: grabbing; }

/* --- SmoothUI Siri-Orb Conic Gradient Animation --- */
.siri-orb {
  display: grid;
  grid-template-areas: "stack";
  overflow: hidden;
  border-radius: 50%;
  position: relative;
}

.siri-orb::before,
.siri-orb::after {
  content: "";
  display: block;
  grid-area: stack;
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.siri-orb::before {
  background:
    conic-gradient(
      from calc(var(--angle) * 2) at 25% 70%,
      var(--c3),
      transparent 20% 80%,
      var(--c3)
    ),
    conic-gradient(
      from calc(var(--angle) * 2) at 45% 75%,
      var(--c2),
      transparent 30% 60%,
      var(--c2)
    ),
    conic-gradient(
      from calc(var(--angle) * -3) at 80% 20%,
      var(--c1),
      transparent 40% 60%,
      var(--c1)
    ),
    conic-gradient(
      from calc(var(--angle) * 2) at 15% 5%,
      var(--c2),
      transparent 10% 90%,
      var(--c2)
    ),
    conic-gradient(
      from calc(var(--angle) * 1) at 20% 80%,
      var(--c1),
      transparent 10% 90%,
      var(--c1)
    ),
    conic-gradient(
      from calc(var(--angle) * -2) at 85% 10%,
      var(--c3),
      transparent 20% 80%,
      var(--c3)
    );
  box-shadow: inset rgba(10,10,16,0.8) 0 0 20px 4px;
  filter: blur(16px) contrast(1.6);
  animation: orb-rotate 20s linear infinite;
}

.siri-orb::after {
  background-image: radial-gradient(
    circle at center,
    rgba(10,10,16,0.85) 12px,
    transparent 12px
  );
  background-size: 24px 24px;
  backdrop-filter: blur(32px) contrast(3);
  mix-blend-mode: overlay;
  mask-image: radial-gradient(black 15%, transparent 75%);
  -webkit-mask-image: radial-gradient(black 15%, transparent 75%);
}

/* State modifiers */
.siri-orb.listening::before {
  animation-duration: 8s;
  filter: blur(12px) contrast(2);
}
.siri-orb.thinking::before {
  animation-duration: 12s;
  filter: blur(14px) contrast(1.8);
}
.siri-orb.answering::before {
  animation-duration: 15s;
}
.siri-orb.offline::before {
  animation-duration: 40s;
  filter: blur(20px) contrast(1.2) saturate(0.3);
}

/* Glow ring */
.orb-glow {
  position: absolute;
  inset: -12px;
  border-radius: 50%;
  pointer-events: none;
  transition: box-shadow 0.6s ease;
}
.orb-glow.idle     { box-shadow: 0 0 40px 12px rgba(120,160,255,0.15); }
.orb-glow.listening { box-shadow: 0 0 50px 20px rgba(0,224,198,0.25); }
.orb-glow.thinking { box-shadow: 0 0 50px 20px rgba(168,85,247,0.25); }
.orb-glow.answering{ box-shadow: 0 0 50px 20px rgba(52,211,153,0.25); }
.orb-glow.offline  { box-shadow: 0 0 20px 6px rgba(100,100,100,0.08); }

@keyframes orb-rotate {
  to { --angle: 360deg; }
}

@media (prefers-reduced-motion: reduce) {
  .siri-orb::before { animation: none; }
}

.hint-text {
  margin-top: 12px;
  color: rgba(255,255,255,0.6);
  font-size: 12px;
  letter-spacing: 0.04em;
  text-align: center;
  transition: all 0.3s;
}
.listening-hint {
  color: rgba(0,224,198,0.8);
  animation: hint-pulse 1.5s ease-in-out infinite;
}
@keyframes hint-pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

@media (max-width: 640px) {
  .smart-orb-root {
    right: 18px;
    bottom: 18px;
  }
  .smart-orb-root.chat-open.has-business-panel:not(.is-dragging) {
    left: 18px;
    right: auto;
  }
  .smart-orb-root.chat-open.has-business-panel:not(.is-dragging) .dialog-panel {
    left: 0;
    right: auto;
  }
  .dialog-panel {
    width: calc(100vw - 36px);
    max-height: 62vh;
  }
  .idle-prompt-bubble {
    max-width: calc(100vw - 130px);
  }
}
</style>
